import { can } from '../utils/permissions';
import * as XLSX from 'xlsx';
import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import inventoryService from '../services/inventoryService';

// ─── UI-only constants ────────────────────────────────────────────────────────
const ISSUED_TO_OPTS = [
  'Kitchen - Chef Kumar', 'Kitchen - Assistant Chef',
  'Housekeeping - Team A', 'Housekeeping - Team B',
  'Maintenance', 'Front Desk', 'Mess Committee', 'Canteen Manager',
];
const PURPOSE_OPTS = [
  'Daily Consumption', 'Special Event', 'Hostel Function',
  'Emergency Use', 'Maintenance Work', 'Other',
];
const CATEGORIES   = ['All Categories','Beverages','Snacks','Groceries','Raw Materials','Packaged Foods','Disposables'];
const UNITS        = ['kg','liters','pieces','packets','boxes','grams','dozen'];
const DEPARTMENTS  = ['Kitchen','Mess','Housekeeping','Maintenance','Front Desk','Administration'];
const TODAY        = new Date().toISOString().split('T')[0];

// ─── Status helpers ───────────────────────────────────────────────────────────
const getStatus = (item) => {
  const stock   = item.currentStock  ?? 0;
  const reorder = item.reorderLevel  ?? 0;
  if (stock <= 0)        return { label:'Out of Stock', cls:'out' };
  if (stock <= reorder)  return { label:'Low Stock',    cls:'low' };
  return                        { label:'In Stock',     cls:'ok'  };
};

const SBG = { ok:['#D4EDDA','#155724'], low:['#FFF3CD','#856404'], out:['#F8D7DA','#721C24'] };
const RBG = { Pending:['#FFF3CD','#856404'], Approved:['#D4EDDA','#155724'], Rejected:['#F8D7DA','#721C24'], Closed:['#F1EFE8','#444441'], Issued:['#D4EDDA','#155724'] };

// ─── Small UI helpers ─────────────────────────────────────────────────────────
const Badge = ({ label, type }) => {
  const [bg, col] = type === 'req'
    ? (RBG[label] || ['#eee','#333'])
    : (SBG[label === 'In Stock' ? 'ok' : label === 'Low Stock' ? 'low' : 'out']);
  return <span style={{ padding:'5px 13px', borderRadius:20, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:.5, background:bg, color:col, display:'inline-block' }}>{label}</span>;
};

const Th = ({ ch }) => (
  <th style={{ padding:'16px 18px', textAlign:'left', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#fff', whiteSpace:'nowrap', background:'#1B3B5F' }}>{ch}</th>
);
const Td = ({ children, s }) => (
  <td style={{ padding:'14px 18px', fontSize:13, borderBottom:'1px solid #E8E4DF', verticalAlign:'middle', ...s }}>{children}</td>
);

const IS = { width:'100%', padding:'11px 14px', border:'2px solid #E8E4DF', borderRadius:6, fontSize:14, fontFamily:'Work Sans,sans-serif', outline:'none', boxSizing:'border-box' };
const LS = { display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 };

const FieldRow = ({ label, value, big, color }) => (
  <div>
    <span style={{ color:'#6B6B6B', fontSize:11, textTransform:'uppercase', letterSpacing:.5, display:'block', marginBottom:3 }}>{label}</span>
    <div style={{ fontWeight:big?700:600, fontSize:big?18:14, color:color||'#2C2C2C' }}>{value||'—'}</div>
  </div>
);

const SecHead = ({ children }) => (
  <h3 style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', margin:'22px 0 12px', borderBottom:'2px solid #E8E4DF', paddingBottom:8 }}>{children}</h3>
);

const Spinner = () => (
  <div style={{ display:'flex', justifyContent:'center', alignItems:'center', padding:60, color:'#6B6B6B', fontSize:14 }}>
    <span style={{ marginRight:10 }}>⏳</span> Loading...
  </div>
);

const ErrBanner = ({ msg, onClose }) => !msg ? null : (
  <div style={{ background:'#F8D7DA', border:'1px solid #F5C2C7', borderRadius:8, padding:'12px 16px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center', color:'#721C24', fontSize:13 }}>
    <span>⚠ {msg}</span>
    <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#721C24' }}>×</button>
  </div>
);

const Toast = ({ toast }) => {
  if (!toast) return null;
  const isSuccess = toast.type !== 'error';
  return (
    <div style={{
      position:'fixed', top:24, right:32, zIndex:9999,
      background: isSuccess ? '#1B3B5F' : '#DC3545',
      color:'#fff', borderRadius:12,
      padding:'16px 22px', minWidth:280, maxWidth:400,
      boxShadow:'0 8px 32px rgba(0,0,0,0.22)',
      display:'flex', alignItems:'flex-start', gap:14,
      animation:'slideInToast .3s ease',
    }}>
      <span style={{ fontSize:22, lineHeight:1, flexShrink:0 }}>{isSuccess ? '✅' : '❌'}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:3, textTransform:'uppercase', letterSpacing:.5, opacity:.85 }}>
          {isSuccess ? 'Success' : 'Error'}
        </div>
        <div style={{ fontSize:14, lineHeight:1.5 }}>{toast.msg}</div>
      </div>
      <div style={{ width:3, position:'absolute', left:0, top:0, bottom:0, background: isSuccess ? '#D4AF37' : '#FF6B6B', borderRadius:'12px 0 0 12px' }}/>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
/* ───── PAGINATION (matches Admission screen style) ───── */
const Pagination = ({ page, total, perPage, onPage, onPerPage }) => {
  const totalPages = Math.ceil(total / perPage);
  if (total === 0) return null;
  const start = page * perPage + 1;
  const end   = Math.min((page + 1) * perPage, total);
  const pages = Array.from({ length: totalPages }, (_, i) => i);
  const Btn = ({ label, onClick, disabled, active }) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding:'7px 11px', border: active ? 'none' : '1px solid #E8E4DF', borderRadius: 4, minWidth: 32,
      background: active ? '#1B3B5F' : disabled ? '#F8F6F3' : '#fff',
      color: active ? '#fff' : disabled ? '#ccc' : '#2C2C2C',
      cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: active ? 700 : 400,
    }}>{label}</button>
  );

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 40px', borderTop:'1px solid #E8E4DF', background:'#fff' }}>
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <Btn label="«" onClick={()=>onPage(0)} disabled={page===0}/>
        <Btn label="‹" onClick={()=>onPage(page-1)} disabled={page===0}/>
        {pages.map(i => <Btn key={i} label={i+1} onClick={()=>onPage(i)} active={i===page}/>)}
        <Btn label="›" onClick={()=>onPage(page+1)} disabled={page>=totalPages-1}/>
        <Btn label="»" onClick={()=>onPage(totalPages-1)} disabled={page>=totalPages-1}/>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#6B6B6B' }}>
        <span>Showing {start}–{end} of {total} &nbsp;|&nbsp; Per page</span>
        {[10,20,30,50].map(s => (
          <button key={s} onClick={()=>{ onPerPage(s); onPage(0); }} style={{
            padding:'5px 11px', border:'none', borderRadius:4, cursor:'pointer', fontSize:13, fontWeight:600,
            background: perPage===s ? '#D4AF37' : '#F0EDE8', color: perPage===s ? '#fff' : '#888',
          }}>{s}</button>
        ))}
      </div>
    </div>
  );
};

const InventoryScreen = ({ onNavigate, onLogout }) => {
  const [tab, setTab] = useState(() => {
    try {
      const role = JSON.parse(localStorage.getItem('user')||'{}')?.role?.toUpperCase();
      if (role === 'CANTEEN_INCHARGE') return 'purchase';
      if (role === 'STORE_INCHARGE')   return 'stockin';
      return 'inventory';
    } catch { return 'inventory'; }
  });
  const resetPages = () => { setInvPage(0); setSiPage(0); setSoPage(0); setPrPage(0); setCrPage(0); };

  // ── Loading / error
  const [loading, setLoading] = useState({ items:true, stockIn:false, stockOut:false, purchase:false, consumption:false });
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const [fErr,    setFErr]    = useState({});

  // ── Server data
  const [stats,       setStats]       = useState({ totalItems:0, lowStockItems:0, outOfStockItems:0, pendingRequisitions:0 });
  const [inventory,   setInventory]   = useState([]);
  const [suppliers,   setSuppliers]   = useState([]);
  const [stockIn,     setStockIn]     = useState([]);
  const [stockOut,    setStockOut]    = useState([]);
  const [purchase,    setPurchase]    = useState([]);
  const [consumption, setConsumption] = useState([]);

  // ── Filters
  const [invPerPage, setInvPerPage] = useState(10);
  const [invPage,  setInvPage]  = useState(0);
  const [siPage,   setSiPage]   = useState(0);
  const [soPage,   setSoPage]   = useState(0);
  const [prPage,   setPrPage]   = useState(0);
  const [crPage,   setCrPage]   = useState(0);
  const [search,   setSearch]   = useState('');
  const [catF,     setCatF]     = useState('All Categories');
  const [stkF,     setStkF]     = useState('All Stock Status');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  // ── Modals
  const [siModal,  setSiModal]  = useState(null);
  const [soModal,  setSoModal]  = useState(null);
  const [siDetail, setSiDetail] = useState(null);
  const [soDetail, setSoDetail] = useState(null);
  const [prCreate, setPrCreate]   = useState(false);
  const [directModal, setDirectModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showCRArchived, setShowCRArchived] = useState(false);
  const [directReason, setDirectReason] = useState('');
  const [directF, setDirectF] = useState({ item:'', qty:'', unit:'', unitPrice:'', invoice:'', date:TODAY, receivedBy:'', supplier:'', note:'' });
  const [prDetail, setPrDetail] = useState(null);
  const [crCreate, setCrCreate] = useState(false);
  const [crDetail, setCrDetail] = useState(null);
  const [addItem,     setAddItem]     = useState(false);

  // ── Form blanks
  const emptySI = { qty:'', unitPrice:'', supplierCode:'', newSupName:'', newSupContact:'', newSupPhone:'', invoice:'', date:TODAY, receivedBy:'', remarks:'' };
  const emptySO = { qty:'', issuedTo:'', purpose:'', date:TODAY, authorizedBy:'', remarks:'' };
  const emptyPR = { item:'', newItemName:'', newItemCat:'Beverages', newItemUnit:'kg', qty:'', unitPrice:'', supplierCode:'', newSupplierName:'', newSupplierContact:'', newSupplierPhone:'', showAddSupplier:false, reason:'Reorder (Low Stock)', dateRequired:'', remarks:'' };
  const emptyCR = { item:'', qty:'', department:'', purpose:'', dateRequired:TODAY, requestedBy:'', remarks:'' };
  const emptyAI = { code:'', name:'', category:'Beverages', unit:'kg', stock:'', reorder:'' };

  // ── Toast helper ──────────────────────────────────────
  const cap = v => v ? v.charAt(0).toUpperCase() + v.slice(1) : v;

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Field validation helpers ───────────────────────────
  const clearErr = () => setFErr({});
  const ErrMsg = ({ name }) => fErr[name]
    ? <div style={{ color:'#DC3545', fontSize:11, marginTop:4, fontWeight:600 }}>⚠ {fErr[name]}</div>
    : null;
  const errBorder = (name) => fErr[name] ? { ...IS, borderColor:'#DC3545' } : IS;

  const [siF, setSiF] = useState(emptySI);
  const [soF, setSoF] = useState(emptySO);
  const [prF, setPrF] = useState(emptyPR);
  const [crF, setCrF] = useState(emptyCR);
  const [aiF, setAiF] = useState(emptyAI);

  // ══════════════════════════════════════════════════════
  // DATA LOADERS
  // ══════════════════════════════════════════════════════

  const loadStats = useCallback(async () => {
    try { setStats(await inventoryService.getStats()); }
    catch (e) { console.error('Stats load failed', e); }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(p => ({ ...p, items:true }));
    try {
      const params = {};
      if (search && search.length >= 2) params.search = search;
      else if (catF && catF !== 'All Categories') params.category = catF;
      let data = await inventoryService.getItems(params);
      if (stkF && stkF !== 'All Stock Status') {
        data = data.filter(i => (i.stockStatus || getStatus(i).label) === stkF);
      }
      setInventory(data);
    } catch (e) { setError('Failed to load inventory items'); }
    finally { setLoading(p => ({ ...p, items:false })); }
  }, [search, catF, stkF]);

  const loadSuppliers = useCallback(async () => {
    try { setSuppliers(await inventoryService.getSuppliers()); }
    catch (e) { console.error('Suppliers load failed', e); }
  }, []);

  const loadStockIn = useCallback(async () => {
    setLoading(p => ({ ...p, stockIn:true }));
    try { setStockIn(await inventoryService.getStockIn()); }
    catch (e) { setError('Failed to load stock-in records'); }
    finally { setLoading(p => ({ ...p, stockIn:false })); }
  }, []);

  const loadStockOut = useCallback(async () => {
    setLoading(p => ({ ...p, stockOut:true }));
    try { setStockOut(await inventoryService.getStockOut()); }
    catch (e) { setError('Failed to load stock-out records'); }
    finally { setLoading(p => ({ ...p, stockOut:false })); }
  }, []);

  const loadPurchase = useCallback(async () => {
    setLoading(p => ({ ...p, purchase:true }));
    try { setPurchase(await inventoryService.getPurchaseReqs()); }
    catch (e) { setError('Failed to load purchase requisitions'); }
    finally { setLoading(p => ({ ...p, purchase:false })); }
  }, []);

  const loadConsumption = useCallback(async () => {
    setLoading(p => ({ ...p, consumption:true }));
    try { setConsumption(await inventoryService.getConsumptionReqs()); }
    catch (e) { setError('Failed to load consumption requisitions'); }
    finally { setLoading(p => ({ ...p, consumption:false })); }
  }, []);

  // Initial load
  useEffect(() => {
    loadStats();
    loadItems();
    loadSuppliers();
  }, []); // eslint-disable-line

  // Filter change reload
  useEffect(() => { loadItems(); }, [search, catF, stkF]); // eslint-disable-line

  // Lazy tab loading
  useEffect(() => {
    if (tab === 'stockin'     && stockIn.length === 0)     loadStockIn();
    if (tab === 'stockout'    && stockOut.length === 0)    loadStockOut();
    if (tab === 'purchase')                                { loadPurchase(); loadSuppliers(); }
    if (tab === 'consumption')                             loadConsumption();
    if (tab === 'alerts')                                  loadItems();
  }, [tab]); // eslint-disable-line

  // ══════════════════════════════════════════════════════
  // SUBMIT HANDLERS
  // ══════════════════════════════════════════════════════

  const submitStockIn = async () => {
    const e = {};
    if (!siF.qty || +siF.qty <= 0)                     e.qty         = 'Quantity is required and must be > 0';
    if (!siF.date)                                     e.date        = 'Received date is required';
    if (!siF.receivedBy)                               e.receivedBy  = 'Received by is required';
    if (siF.supplierCode === 'new' && !siF.newSupName) e.newSupName  = 'Supplier name is required';
    if (Object.keys(e).length) { setFErr(e); return; }
    clearErr();
    setSaving(true);
    try {
      const payload = {
        itemCode:           siModal.itemCode,
        quantity:           +siF.qty,
        unitPrice:          siF.unitPrice ? +siF.unitPrice : null,
        supplierCode:       siF.supplierCode && siF.supplierCode !== 'new' ? siF.supplierCode : null,
        supplierNameManual: siF.supplierCode === 'new' ? siF.newSupName : null,
        supplierContact:    siF.newSupContact || null,
        supplierPhone:      siF.newSupPhone   || null,
        invoiceNumber:      siF.invoice       || null,
        receivedDate:       siF.date,
        receivedBy:         siF.receivedBy    || null,
        requisitionId:      siModal._prId ? siModal._prReqId : null,
        type:               siModal._prId ? 'PR-linked' : (siModal._directType || 'Direct'),
        remarks:            siF.remarks       || null,
      };
      const created = await inventoryService.recordStockIn(payload);
      setStockIn(p => [created, ...p]);
      // If opened from PR Receive Stock — close the PR → moves to archive
      if (siModal._prId) {
        const prIdToClose = siModal._prId;
        try {
          await inventoryService.closePurchaseReq(prIdToClose);
        } catch(closeErr) {
          console.error('Failed to close PR:', closeErr);
          showToast('Stock added but PR close failed.', 'error');
        }
        // Always reload from server to get real DB status
        await loadPurchase();
      }
      await Promise.all([loadItems(), loadStats(), loadSuppliers()]);
      setSiModal(null); setSiF(emptySI); clearErr();
      showToast(`✓ Stock added! New stock: ${created.newStock} ${created.unit}${siModal._prId ? ` · ${siModal._prReqId} closed.` : ''}`);
    } catch (e) {
      showToast('Failed to record stock-in: ' + (e.response?.data?.message || e.message), 'error');
    } finally { setSaving(false); }
  };

  const submitStockOut = async () => {
    const available = soModal.currentStock ?? 0;
    const e = {};
    if (!soF.qty || +soF.qty <= 0)  e.qty      = 'Quantity is required and must be > 0';
    else if (+soF.qty > available)  e.qty      = `Cannot issue more than ${available} ${soModal.unit}`;
    if (!soF.issuedTo)              e.issuedTo = 'Please select who stock is issued to';
    if (!soF.purpose)               e.purpose  = 'Please select a purpose';
    if (Object.keys(e).length) { setFErr(e); return; }
    clearErr();
    setSaving(true);
    try {
      const payload = {
        itemCode:      soModal.itemCode,
        quantity:      +soF.qty,
        issuedTo:      soF.issuedTo,
        purpose:       soF.purpose,
        issueDate:     soF.date,
        authorizedBy:  soF.authorizedBy || null,
        requisitionId: soModal._crReqId || null,
        remarks:       soF.remarks      || null,
      };
      const created = await inventoryService.recordStockOut(payload);
      setStockOut(p => [created, ...p]);
      // If issued against a CR — mark CR as Issued → moves to archive
      if (soModal._crId) {
        const crIdToClose = soModal._crId;
        try {
          await inventoryService.actionConsumptionReq(crIdToClose, 'Issued', 'Admin', 'Stock issued');
        } catch(crErr) {
          console.error('Failed to close CR:', crErr);
        }
        // Always reload from server to get real DB status
        await loadConsumption();
      }
      await Promise.all([loadItems(), loadStats()]);
      setSoModal(null); setSoF(emptySO); clearErr();
      const warn = created.lowStock ? '\n⚠️ Stock now below reorder level!' : '';
      showToast(`✓ Stock issued! ${soF.qty} ${soModal.unit} issued. Remaining: ${created.remStock} ${created.unit}${warn}${soModal._crId ? ` · ${soModal._crReqId} closed.` : ''}`);
    } catch (e) {
      showToast('Failed to record stock-out: ' + (e.response?.data?.message || e.message), 'error');
    } finally { setSaving(false); }
  };

  const submitPR = async () => {
    const e = {};
    if (!prF.item)                                e.item        = 'Please select an item';
    if (!prF.qty || +prF.qty <= 0)               e.qty         = 'Quantity is required and must be > 0';
    if (prF.item === 'new' && !prF.newItemName)  e.newItemName = 'Item name is required for new item';
    if (!prF.reason)                              e.reason      = 'Please select a reason';
    if (Object.keys(e).length) { setFErr(e); return; }
    clearErr();
    setSaving(true);
    try {
      const itemName = prF.item === 'new' ? prF.newItemName : prF.item.split(' — ')[1];
      const total    = prF.qty && prF.unitPrice && +prF.unitPrice > 0
        ? `₹${(+prF.qty * +prF.unitPrice).toLocaleString('en-IN', { maximumFractionDigits:2 })}` : '—';
      // Save new supplier to DB if user added one
      let supplierName = '—';
      if (prF.supplierCode === 'new' && prF.newSupplierName) {
        try {
          const saved = await inventoryService.addSupplier({
            name: prF.newSupplierName,
            contactPerson: prF.newSupplierContact || null,
            phone: prF.newSupplierPhone || null,
          });
          await loadSuppliers(); // refresh dropdown
          supplierName = saved.name;
          // Auto-select newly added supplier and close inline form
          setPrF(p => ({ ...p, supplierCode: saved.supplierCode, showAddSupplier: false,
            newSupplierName: '', newSupplierContact: '', newSupplierPhone: '' }));
        } catch(supErr) {
          supplierName = prF.newSupplierName; // fallback to name even if save fails
        }
      } else if (prF.supplierCode) {
        supplierName = suppliers.find(s => s.supplierCode === prF.supplierCode)?.name || prF.supplierCode;
      }
      const created = await inventoryService.createPurchaseReq({
        itemName,
        quantity:          `${prF.qty} units`,
        unitPrice:         prF.unitPrice ? `₹${(+prF.unitPrice).toFixed(2)}` : '—',
        totalCost:         total,
        preferredSupplier: supplierName,
        reason:            prF.reason,
        dateRequired:      prF.dateRequired || null,
        remarks:           prF.remarks      || null,
      });
      setPurchase(p => [created, ...p]);
      await loadStats();
      setPrCreate(false); setPrF(emptyPR); clearErr();
      showToast('✓ Purchase Requisition submitted for approval.');
    } catch (e) {
      showToast('Failed to submit PR: ' + (e.response?.data?.message || e.message), 'error');
    } finally { setSaving(false); }
  };

  const submitAddItem = async () => {
    const e = {};
    if (!aiF.code)  e.code = 'Item code is required';
    if (!aiF.name)  e.name = 'Item name is required';
    if (aiF.stock && +aiF.stock < 0)   e.stock  = 'Stock cannot be negative';
    if (aiF.reorder && +aiF.reorder < 0) e.reorder = 'Reorder level cannot be negative';
    if (Object.keys(e).length) { setFErr(e); return; }
    clearErr();
    setSaving(true);
    try {
      await inventoryService.addItem({
        itemCode:     aiF.code.toUpperCase(),
        name:         aiF.name,
        category:     aiF.category,
        unit:         aiF.unit,
        currentStock: +aiF.stock  || 0,
        reorderLevel: +aiF.reorder || 10,
      });
      await Promise.all([loadItems(), loadStats()]);
      setAddItem(false); setAiF(emptyAI); clearErr();
      showToast('✓ New item added to inventory!');
    } catch (e) {
      showToast('Failed to add item: ' + (e.response?.data?.message || e.message), 'error');
    } finally { setSaving(false); }
  };

  const submitCR = async () => {
    const e = {};
    if (!crF.item)                  e.item        = 'Please select an item';
    if (!crF.qty || +crF.qty <= 0) e.qty         = 'Quantity is required and must be > 0';
    if (!crF.department)            e.department  = 'Please select a department';
    if (!crF.purpose)               e.purpose     = 'Please select a purpose';
    if (!crF.requestedBy)           e.requestedBy = 'Requested by is required';
    if (Object.keys(e).length) { setFErr(e); return; }
    clearErr();
    setSaving(true);
    try {
      const itemName = crF.item.split(' — ')[1] || crF.item;
      const created = await inventoryService.createConsumptionReq({
        itemName,
        quantity:     `${crF.qty}`,
        requestedBy:  crF.requestedBy || 'Store Manager',
        purpose:      crF.purpose,
        department:   crF.department,
        dateRequired: crF.dateRequired,
        remarks:      crF.remarks,
      });
      setConsumption(p => [created, ...p]);
      await loadStats();
      setCrCreate(false); setCrF(emptyCR); clearErr();
      showToast('✓ Consumption Requisition submitted for approval.');
    } catch (e) {
      showToast('Failed to submit CR: ' + (e.response?.data?.message || e.message), 'error');
    } finally { setSaving(false); }
  };

  // Issue stock directly from an Approved CR
  const issueStockForCR = async (cr) => {
    const item = inventory.find(i => i.name === cr.item || i.itemCode === cr.itemCode);
    if (!item) { alert('Cannot find item in inventory. Please issue from the Inventory tab.'); return; }
    setSoModal({ ...item, _crId: cr.id, _crReqId: cr.requisitionId });
    setSoF({ ...emptySO, qty: cr.qty || cr.quantity || '', issuedTo: cr.requestedBy || '', purpose: cr.purpose || 'Daily Consumption', remarks: `CR: ${cr.requisitionId}` });
  };

  const actReq = async (type, id, action, comments = '') => {
    setSaving(true);
    try {
      if (type === 'purchase') {
        const updated = await inventoryService.actionPurchaseReq(id, action, 'Admin', comments);
        setPurchase(p => p.map(r => r.id === id ? updated : r));
        if (prDetail?.id === id) setPrDetail(updated);
      } else {
        const updated = await inventoryService.actionConsumptionReq(id, action, 'Admin', comments);
        setConsumption(p => p.map(r => r.id === id ? updated : r));
      }
      await loadStats();
    } catch (e) {
      showToast('Action failed: ' + (e.response?.data?.message || e.message), 'error');
    } finally { setSaving(false); }
  };

  const handleRejectPR = (type, id) => {
    const reason = window.prompt('Please provide a reason for rejecting this requisition:');
    if (reason !== null) actReq(type, id, 'Rejected', reason);
  };

  // ══════════════════════════════════════════════════════
  // STYLES
  // ══════════════════════════════════════════════════════
  const B  = { padding:'10px 20px', border:'none', borderRadius:6, fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:1, cursor:'pointer', transition:'all .2s' };
  const Bp = { ...B, background:'#1B3B5F', color:'#fff' };
  const Bs = { ...B, background:'#2D5F4F', color:'#fff' };
  const Bw = { ...B, background:'#F59E0B', color:'#fff' };
  const Bd = { ...B, background:'#DC3545', color:'#fff' };
  const Bg = { ...B, background:'#E8E4DF', color:'#2C2C2C' };
  const Bc = { ...B, background:'#E85D4A', color:'#fff' };

  const tabSty = (t) => ({
    padding:'13px 18px', background:'transparent', border:'none',
    borderBottom:`3px solid ${tab === t ? '#D4AF37' : 'transparent'}`,
    fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:1,
    color: tab === t ? '#1B3B5F' : '#6B6B6B', cursor:'pointer', marginBottom:-2,
  });

  const siNew  = siF.qty && siModal ? +((siModal.currentStock ?? 0) + +siF.qty).toFixed(2) : null;
  const soAvail = soModal ? (soModal.currentStock ?? 0) : 0;
  const soRem  = soF.qty && soModal ? +(soAvail - +soF.qty).toFixed(2) : null;
  const soRemC = soRem === null ? '#2C2C2C' : soRem < 0 ? '#DC3545' : soRem <= (soModal?.reorderLevel ?? 0) ? '#F59E0B' : '#2D5F4F';

  const OV  = { position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 };
  const MB  = { background:'#fff', borderRadius:12, boxShadow:'0 20px 60px rgba(0,0,0,0.25)', display:'flex', flexDirection:'column', maxHeight:'92vh' };
  const MH  = { padding:'24px 28px', borderBottom:'1px solid #E8E4DF', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 };
  const MBd = { overflowY:'auto', padding:'24px 28px', flex:1 };
  const MF  = { padding:'16px 28px', borderTop:'1px solid #E8E4DF', flexShrink:0, display:'flex', gap:12 };
  const MT  = { fontFamily:'Cormorant Garamond,serif', fontSize:24, color:'#1B3B5F', margin:0 };
  const MX  = { background:'none', border:'none', fontSize:26, cursor:'pointer', color:'#9B9B9B', lineHeight:1 };
  const G2  = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 };

  // ══════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════
  // ── Inventory Excel Export ──────────────────────────────────────────────
  const downloadInventoryExcel = () => {
    let rows = [];
    let sheet = 'Data';
    let file  = 'inventory';

    // Date filter helper
    const from = dateFrom ? new Date(dateFrom) : null;
    const to   = dateTo   ? new Date(dateTo + 'T23:59:59') : null;
    const inRange = (dateStr) => {
      if (!dateStr) return true;
      const d = new Date(dateStr);
      if (from && d < from) return false;
      if (to   && d > to)   return false;
      return true;
    };

    if (tab === 'inventory') {
      sheet = 'Inventory Items'; file = 'inventory_items';
      // No date filter for inventory items
      rows = inventory.map(r => ({
        'Item Code':     r.itemCode      || '',
        'Item Name':     r.itemName      || r.name || '',
        'Category':      r.category      || '',
        'Current Stock': r.currentStock  ?? 0,
        'Unit':          r.unit          || '',
        'Reorder Level': r.reorderLevel  ?? 0,
        'Status':        (r.currentStock??0) <= 0 ? 'Out of Stock' : (r.currentStock??0) <= (r.reorderLevel??0) ? 'Low Stock' : 'In Stock',
      }));

    } else if (tab === 'stockin') {
      sheet = 'Stock In'; file = 'stock_in';
      rows = stockIn
        .filter(r => inRange(r.date || r.stockInDate))
        .map(r => ({
          'Date':         r.date          || r.stockInDate  || '',
          'Item':         r.item          || r.itemName     || '',
          'Qty Received': r.qty           || r.quantity     || 0,
          'Unit':         r.unit          || '',
          'Type':         r.requisitionId ? 'PR-linked' : (r.type || 'Direct'),
          'PR Reference': r.requisitionId || '',
          'Invoice No.':  r.invoice       || r.invoiceNumber|| '',
          'Received By':  r.receivedBy    || '',
        }));

    } else if (tab === 'stockout') {
      sheet = 'Stock Out'; file = 'stock_out';
      rows = stockOut
        .filter(r => inRange(r.date || r.issueDate))
        .map(r => ({
          'Date':         r.date          || r.issueDate    || '',
          'Item':         r.item          || r.itemName     || '',
          'Qty Issued':   r.qty           || r.quantity     || 0,
          'Unit':         r.unit          || '',
          'CR Reference': r.requisitionId || '',
          'Department':   r.issuedTo      || '',
          'Purpose':      r.purpose       || '',
          'Issued By':    r.issuedByPerson|| r.authorizedBy || '',
        }));

    } else if (tab === 'purchase') {
      sheet = 'Purchase Requisitions'; file = 'purchase_req';
      rows = purchase
        .filter(r => inRange(r.date || r.createdAt))
        .map(r => ({
          'Req. ID':      r.requisitionId || '',
          'Item Name':    r.item          || r.itemName     || '',
          'Quantity':     r.qty           || r.quantity     || 0,
          'Requested By': r.requestedBy   || '',
          'Date':         r.date          || r.createdAt    || '',
          'Status':       r.status        || '',
        }));

    } else if (tab === 'consumption') {
      sheet = 'Consumption Requisitions'; file = 'consumption_req';
      rows = consumption
        .filter(r => inRange(r.date || r.createdAt))
        .map(r => ({
          'Req. ID':       r.requisitionId || '',
          'Item Name':     r.itemName      || r.item        || '',
          'Qty Requested': r.qty           || r.quantity    || 0,
          'Current Stock': r.currentStock  ?? '',
          'Requested By':  r.requestedBy   || '',
          'Purpose':       r.purpose       || '',
          'Date':          r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : (r.date||''),
          'Status':        r.status        || '',
        }));
    }

    if (!rows.length) { alert('No data found for selected date range.'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    const suffix = (dateFrom || dateTo)
      ? '_' + (dateFrom||'start') + '_to_' + (dateTo||'end')
      : '_' + new Date().toISOString().slice(0,10);
    XLSX.writeFile(wb, file + suffix + '.xlsx');
  };

  return (
    <>
    <div className="app-layout">
      <Sidebar activeItem="inventory" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ padding:0 }}>

        {/* ── Top Bar ── */}
        <div style={{ background:'#fff', padding:'22px 40px', borderBottom:'1px solid #E8E4DF', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:50 }}>
          <h1 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:30, fontWeight:400, color:'#1B3B5F', margin:0 }}>Inventory Management</h1>
          <div style={{ display:'flex', gap:12 }}>
            {can('CREATE_PR') && <button style={Bs} onClick={() => setPrCreate(true)}>📋 Purchase Requisition</button>}
            {can('CREATE_CR') && <button style={Bp} onClick={() => setCrCreate(true)}>🍽️ Consumption Requisition</button>}
            {can('CREATE_PR') && <button style={Bp} onClick={() => setAddItem(true)}>+ Add Item</button>}
            {can('RECEIVE_STOCK') && can('CREATE_PR') && <button style={{ ...Bs, background:'#D4AF37', color:'#1B3B5F', border:'none' }} onClick={async () => { setDirectModal(true); setDirectReason(''); setDirectF({ item:'', qty:'', unit:'', unitPrice:'', invoice:'', date:TODAY, receivedBy:'', supplier:'', note:'' }); await loadSuppliers(); }}>+ Direct Stock In</button>}
          </div>
        </div>

        <div style={{ padding:'32px 40px' }}>
          <ErrBanner msg={error} onClose={() => setError('')} />

          {/* ── Overview Cards ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:24, marginBottom:36 }}>
            {[
              { icon:'📦', label:'Total Items',          val:stats.totalItems,          sub:`Across ${CATEGORIES.length-1} categories`, acc:'#D4AF37' },
              { icon:'⚠️', label:'Low Stock Items',      val:stats.lowStockItems,       sub:'Below reorder level',                       acc:'#F59E0B' },
              { icon:'⏰', label:'Pending Requisitions', val:stats.pendingRequisitions, sub:'Awaiting approval',                         acc:'#DC3545' },
              { icon:'📤', label:'Out of Stock',         val:stats.outOfStockItems,     sub:'Need immediate restocking',                 acc:'#2D5F4F' },
            ].map(c => (
              <div key={c.label} style={{ background:'#fff', padding:28, borderRadius:12, boxShadow:'0 4px 20px rgba(0,0,0,0.07)', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:c.acc }}/>
                <div style={{ fontSize:26, marginBottom:12 }}>{c.icon}</div>
                <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>{c.label}</div>
                <div style={{ fontSize:32, fontWeight:700, fontFamily:'Cormorant Garamond,serif', color:'#1B3B5F', marginBottom:2 }}>{c.val}</div>
                <div style={{ fontSize:12, color:'#6B6B6B' }}>{c.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Tabs ── */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:28 }}>
            <div style={{ display:'flex', gap:2, borderBottom:'2px solid #E8E4DF' }}>
              {(() => {
                const role = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}')?.role?.toUpperCase(); } catch { return ''; } })();
                const isHousekeeping  = role === 'HOUSEKEEPING';
                // ✅ STORE_INCHARGE — full access to all inventory tabs
                const isStoreIncharge = role === 'STORE_INCHARGE';
                return [
                  // Inventory List — hide for HOUSEKEEPING, STORE_INCHARGE only
                  (!isHousekeeping && (can('CREATE_PR')||can('APPROVE_PR')||can('CREATE_CR')||can('APPROVE_CR')||isStoreIncharge)) ? ['inventory','Inventory List'] : null,
                  // Stock In — hide for HOUSEKEEPING only
                  (!isHousekeeping && (can('RECEIVE_STOCK')||isStoreIncharge)) ? ['stockin','Stock In'] : null,
                  // Stock Out — hide for HOUSEKEEPING only
                  (!isHousekeeping && (can('ISSUE_STOCK')||isStoreIncharge))   ? ['stockout','Stock Out'] : null,
                  // Purchase Reqs
                  (can('CREATE_PR')||can('APPROVE_PR')) ? ['purchase','Purchase Reqs'] : null,
                  // Consumption Reqs
                  (!isHousekeeping && (can('CREATE_CR')||can('APPROVE_CR')||isStoreIncharge)) ? ['consumption','Consumption Reqs'] : null,
                  // Stock Alerts — hide for HOUSEKEEPING, STORE_INCHARGE only
                  (!isHousekeeping && (can('CREATE_PR')||can('APPROVE_PR')||can('CREATE_CR')||can('APPROVE_CR')||isStoreIncharge)) ? ['alerts','Stock Alerts'] : null,
                ].filter(Boolean).map(([id,label]) => (
                  <button key={id} style={tabSty(id)} onClick={() => { setTab(id); resetPages(); }}>{label}</button>
                ));
              })()}
            </div>
            <div style={{ paddingBottom:16, display:'flex', gap:10, alignItems:'center' }}>
              <span style={{ fontSize:12, fontWeight:600, color:'#6B6B6B', whiteSpace:'nowrap' }}>Date Range:</span>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding:'7px 10px', border:'2px solid #E8E4DF', borderRadius:6, fontSize:12 }}/>
              <span style={{ color:'#6B6B6B' }}>to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding:'7px 10px', border:'2px solid #E8E4DF', borderRadius:6, fontSize:12 }}/>
              <button style={{ ...Bs, padding:'9px 16px', fontSize:12, display:'flex', alignItems:'center', gap:8 }} onClick={downloadInventoryExcel}>
                <span style={{ fontSize:16 }}>📥</span>
                <span>Excel</span>
              </button>
            </div>
          </div>

          {/* ══ INVENTORY LIST ══ */}
          {tab === 'inventory' && (<>
            <div style={{ background:'#fff', padding:'14px 18px', borderRadius:12, marginBottom:18, display:'flex', gap:14, alignItems:'center', boxShadow:'0 2px 10px rgba(0,0,0,0.05)' }}>
              <div style={{ flex:1, position:'relative' }}>
                <input style={IS} placeholder="Search by item name or code..." value={search} onChange={e => setSearch(e.target.value)}/>
                <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', color:'#9B9B9B' }}>🔍</span>
              </div>
              <select style={{ padding:'11px 14px', border:'2px solid #E8E4DF', borderRadius:6, fontSize:13, fontWeight:600, minWidth:170 }} value={catF} onChange={e => setCatF(e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <select style={{ padding:'11px 14px', border:'2px solid #E8E4DF', borderRadius:6, fontSize:13, fontWeight:600, minWidth:160 }} value={stkF} onChange={e => setStkF(e.target.value)}>
                {['All Stock Status','In Stock','Low Stock','Out of Stock'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.07)' }}>
              {loading.items ? <Spinner/> : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Item Code','Item Name','Category','Current Stock','Unit','Status','Actions'].map(h => <Th key={h} ch={h}/>)}</tr></thead>
                  <tbody>
                    {inventory.slice(invPage*invPerPage, (invPage+1)*invPerPage).map(item => {
                      const st    = getStatus(item);
                      const stock = item.currentStock ?? 0;
                      return (
                        <tr key={item.id} onMouseEnter={e => e.currentTarget.style.background='#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                          <Td><span style={{ fontFamily:'monospace', fontWeight:600, color:'#1B3B5F' }}>{item.itemCode}</span></Td>
                          <Td><strong>{item.name}</strong></Td>
                          <Td>{item.category}</Td>
                          <Td><strong style={{ color:st.cls==='out'?'#DC3545':st.cls==='low'?'#F59E0B':'#2C2C2C' }}>{stock}</strong></Td>
                          <Td>{item.unit}</Td>
                          <Td><Badge label={st.label}/></Td>
                          <Td><button style={{ padding:'5px 11px', background:'#fff', color:'#1B3B5F', border:'1px solid #E8E4DF', borderRadius:6, fontSize:11, fontWeight:500, cursor:'pointer' }} onClick={() => setCrCreate(true)}>+ Raise CR</button></Td>
                        </tr>
                      );
                    })}
                    {inventory.length === 0 && !loading.items && (
                      <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#9B9B9B', fontSize:14 }}>No items found</td></tr>
                    )}
                  </tbody>
                </table>
              )}
              <Pagination page={invPage} total={inventory.length} perPage={invPerPage} onPage={setInvPage} onPerPage={setInvPerPage}/>
            </div>
          </>)}

          {/* ══ STOCK IN ══ */}
          {tab === 'stockin' && (
            <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.07)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background:'#e8f0fb', borderBottom:'1px solid #b8d0f5', fontSize:12.5, color:'#1a3f7a' }}>
                <span>ℹ️</span>
                <span>This is a read-only ledger. All stock-in entries are created automatically when stock is received against a PR, or via Direct Stock In.</span>
              </div>
              {loading.stockIn ? <Spinner/> : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Date','Item','Qty Received','Unit','Type','PR Reference','Invoice No.','Received By'].map(h => <Th key={h} ch={h}/>)}</tr></thead>
                  <tbody>
                    {stockIn.slice(siPage*invPerPage, (siPage+1)*invPerPage).map(s => (
                      <tr key={s.id} onMouseEnter={e => e.currentTarget.style.background='#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        <Td>{s.date}</Td>
                        <Td><strong>{s.item}</strong></Td>
                        <Td><strong style={{ color:'#2D5F4F' }}>+{s.qty}</strong></Td>
                        <Td>{s.unit}</Td>
                        <Td>{(() => {
                          const typeMap = {
                            'PR-linked':  { bg:'#e8f5ee', color:'#1a6b3c', border:'#a8d5b8', label:'PR-linked' },
                            'emergency':  { bg:'#fdf0eb', color:'#7a3018', border:'#f5c4a8', label:'Emergency' },
                            'Emergency':  { bg:'#fdf0eb', color:'#7a3018', border:'#f5c4a8', label:'Emergency' },
                            'donation':   { bg:'#f0eeff', color:'#3c2489', border:'#c8bef5', label:'Donation' },
                            'Donation':   { bg:'#f0eeff', color:'#3c2489', border:'#c8bef5', label:'Donation' },
                            'adjustment': { bg:'#fff3cd', color:'#856404', border:'#ffecb5', label:'Adjustment' },
                            'Adjustment': { bg:'#fff3cd', color:'#856404', border:'#ffecb5', label:'Adjustment' },
                            'direct':     { bg:'#e8eef7', color:'#1a4a8a', border:'#b0c4e0', label:'Direct' },
                            'Direct':     { bg:'#e8eef7', color:'#1a4a8a', border:'#b0c4e0', label:'Direct' },
                          };
                          const key = s.requisitionId ? 'PR-linked' : (s.type || 'direct');
                          const t = typeMap[key] || { bg:'#e8eef7', color:'#1a4a8a', border:'#b0c4e0', label: key };
                          return <span style={{ padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500, background:t.bg, color:t.color, border:`1px solid ${t.border}` }}>{t.label}</span>;
                        })()}</Td>
                        <Td>{s.requisitionId ? <span style={{ padding:'4px 10px', background:'#e8f0fb', border:'1px solid #b8d0f5', borderRadius:20, fontSize:11, color:'#1a3f7a' }}>{s.requisitionId}</span> : <span style={{ color:'#9B9B9B', fontSize:12 }}>— No PR</span>}</Td>
                        <Td s={{ fontFamily:'monospace', fontSize:12 }}>{s.invoice || '—'}</Td>
                        <Td>{s.receivedBy || '—'}</Td>
                      </tr>
                    ))}
                    {stockIn.length === 0 && <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#9B9B9B' }}>No stock-in records</td></tr>}
                  </tbody>
                </table>
              )}
              <Pagination page={siPage} total={stockIn.length} perPage={invPerPage} onPage={setSiPage} onPerPage={setInvPerPage}/>
            </div>
          )}

          {/* ══ STOCK OUT ══ */}
          {tab === 'stockout' && (
            <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.07)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background:'#e8f0fb', borderBottom:'1px solid #b8d0f5', fontSize:12.5, color:'#1a3f7a' }}>
                <span>ℹ️</span>
                <span>This is a read-only ledger. All stock-out entries are created automatically when stock is issued against an approved Consumption Requisition.</span>
              </div>
              {loading.stockOut ? <Spinner/> : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Date','Item','Qty Issued','Unit','CR Reference','Department','Purpose','Issued By'].map(h => <Th key={h} ch={h}/>)}</tr></thead>
                  <tbody>
                    {stockOut.slice(soPage*invPerPage, (soPage+1)*invPerPage).map(s => (
                      <tr key={s.id} onMouseEnter={e => e.currentTarget.style.background='#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        <Td>{s.date}</Td>
                        <Td><strong>{s.item}</strong></Td>
                        <Td><strong style={{ color:'#E85D4A' }}>−{s.qty}</strong></Td>
                        <Td>{s.unit}</Td>
                        <Td>{s.requisitionId ? <span style={{ padding:'4px 10px', background:'#e8f0fb', border:'1px solid #b8d0f5', borderRadius:20, fontSize:11, color:'#1a3f7a' }}>{s.requisitionId}</span> : <span style={{ color:'#9B9B9B', fontSize:12 }}>— No CR</span>}</Td>
                        <Td>{s.issuedTo || '—'}</Td>
                        <Td>{s.purpose || '—'}</Td>
                        <Td>{s.issuedByPerson || s.authorizedBy || '—'}</Td>
                      </tr>
                    ))}
                    {stockOut.length === 0 && <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#9B9B9B' }}>No stock-out records</td></tr>}
                  </tbody>
                </table>
              )}
              <Pagination page={soPage} total={stockOut.length} perPage={invPerPage} onPage={setSoPage} onPerPage={setInvPerPage}/>
            </div>
          )}

          {/* ══ PURCHASE REQS ══ */}
          {tab === 'purchase' && (can('CREATE_PR')||can('APPROVE_PR')||(() => { try { return JSON.parse(localStorage.getItem('user')||'{}')?.role?.toUpperCase() === 'CANTEEN_INCHARGE'; } catch { return false; } })()) && (
            <>
            <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.07)' }}>
              <div style={{ padding:'12px 18px', borderBottom:'1px solid #E8E4DF', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, color:'#6B6B6B' }}>Active purchase requisitions</span>
                <button onClick={() => setShowArchived(a => !a)} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12.5, color: showArchived ? '#1B3B5F' : '#5a6478', cursor:'pointer', padding:'6px 10px', borderRadius:6, border: showArchived ? '1px solid #1B3B5F' : '1px solid #e2ddd5', background:'#fff', fontWeight: showArchived ? 600 : 400 }}>
                  <span style={{ width:28, height:16, background: showArchived ? '#1B3B5F' : '#e2ddd5', borderRadius:8, position:'relative', display:'inline-block', transition:'background 0.2s', flexShrink:0 }}>
                    <span style={{ width:12, height:12, background:'white', borderRadius:'50%', position:'absolute', top:2, left: showArchived ? 14 : 2, transition:'left 0.2s', display:'block' }}/>
                  </span>
                  {showArchived ? 'Showing archived' : 'Show archived'}
                </button>
              </div>
              {loading.purchase ? <Spinner/> : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Req. ID','Item Name','Quantity','Requested By','Date','Status','Actions'].map(h => <Th key={h} ch={h}/>)}</tr></thead>
                  <tbody>
                    {purchase.filter(r => showArchived || !['Closed','Rejected'].includes(r.status)).slice(prPage*invPerPage, (prPage+1)*invPerPage).map(r => (
                      <tr key={r.id}
                        style={{ opacity: ['Closed','Rejected'].includes(r.status) ? 0.55 : 1, fontStyle: ['Closed','Rejected'].includes(r.status) ? 'italic' : 'normal', background: ['Closed','Rejected'].includes(r.status) ? '#F8F6F3' : '#fff' }}
                        onMouseEnter={e => e.currentTarget.style.background='#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background=['Closed','Rejected'].includes(r.status)?'#F8F6F3':'#fff'}>
                        <Td><span onClick={() => setPrDetail(r)} style={{ color:'#1B3B5F', fontWeight:700, cursor:'pointer', borderBottom:'1px dashed #1B3B5F', fontFamily:'monospace' }}>{r.requisitionId}</span></Td>
                        <Td><strong>{r.item}</strong></Td>
                        <Td>{r.qty}</Td><Td>{r.requestedBy}</Td><Td>{r.date}</Td>
                        <Td><Badge label={r.status} type="req"/></Td>
                        <Td>
                          {r.status === 'Pending' && <>
                            {can('APPROVE_PR') && (
                              <button style={{ ...Bs, padding:'6px 11px', fontSize:11, marginRight:6 }} disabled={saving} onClick={() => actReq('purchase',r.id,'Approved')}>Approve</button>
                            )}
                            <button style={{ ...Bd, padding:'6px 11px', fontSize:11 }} disabled={saving} onClick={() => handleRejectPR('purchase',r.id)}>Reject</button>
                          </>}
                          {r.status === 'Approved' && (
                            <button style={{ padding:'6px 11px', background:'#1B3B5F', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer' }} onClick={() => {
                              const item = inventory.find(i => i.name === r.item || i.itemCode === r.item);
                              if (!item) { showToast('Item not found in inventory', 'error'); return; }
                              loadSuppliers();
                              setSiModal({ ...item, _prId: r.id, _prReqId: r.requisitionId, _prQty: r.qty });
                              setSiF({ ...emptySI, remarks: `Received against ${r.requisitionId}` });
                            }}>📥 Receive stock</button>
                          )}
                          {(r.status === 'Rejected' || r.status === 'Closed') && (
                            <span style={{ color:'#9B9B9B', fontSize:12, fontStyle:'italic' }}>
                              {r.status === 'Closed' ? `Received ${r.approvalDate || r.date || ''}` : 'Rejected'}
                            </span>
                          )}
                        </Td>
                      </tr>
                    ))}
                    {purchase.length === 0 && <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#9B9B9B' }}>No purchase requisitions</td></tr>}
                  </tbody>
                </table>
              )}
              <Pagination page={prPage} total={purchase.length} perPage={invPerPage} onPage={setPrPage} onPerPage={setInvPerPage}/>
            </div>
            <p style={{ fontSize:12, color:'#9B9B9B', marginTop:12 }}>
              Closed and rejected PRs are archived. Toggle <strong>"Show archived"</strong> for a quick ops lookup. Full procurement history and analysis is available in the <strong>Reports module</strong>.
            </p>
            </>
          )}

          {/* ══ CONSUMPTION REQS ══ */}
          {tab === 'consumption' && (can('CREATE_CR')||can('APPROVE_CR')||(() => { try { return JSON.parse(localStorage.getItem('user')||'{}')?.role?.toUpperCase() === 'CANTEEN_INCHARGE'; } catch { return false; } })()) && (
            <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.07)' }}>
              <div style={{ padding:'12px 18px', borderBottom:'1px solid #E8E4DF', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, color:'#6B6B6B' }}>Active consumption requisitions</span>
                <button onClick={() => setShowCRArchived(a => !a)} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12.5, color: showCRArchived ? '#1B3B5F' : '#5a6478', cursor:'pointer', padding:'6px 10px', borderRadius:6, border: showCRArchived ? '1px solid #1B3B5F' : '1px solid #e2ddd5', background:'#fff', fontWeight: showCRArchived ? 600 : 400 }}>
                  <span style={{ width:28, height:16, background: showCRArchived ? '#1B3B5F' : '#e2ddd5', borderRadius:8, position:'relative', display:'inline-block', transition:'background 0.2s', flexShrink:0 }}>
                    <span style={{ width:12, height:12, background:'white', borderRadius:'50%', position:'absolute', top:2, left: showCRArchived ? 14 : 2, transition:'left 0.2s', display:'block' }}/>
                  </span>
                  {showCRArchived ? 'Showing archived' : 'Show archived'}
                </button>
              </div>
              {loading.consumption ? <Spinner/> : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Req. ID','Item Name','Qty Requested','Current Stock','Requested By','Purpose','Date','Status','Actions'].map(h => <Th key={h} ch={h}/>)}</tr></thead>
                  <tbody>
                    {consumption.filter(r => showCRArchived || !['Rejected','Issued','Closed'].includes(r.status)).slice(crPage*invPerPage, (crPage+1)*invPerPage).map(r => {
                      const currStock = r.currentStock ?? r.itemCurrentStock ?? null;
                      const reqQty    = Number(r.quantity || r.qty || 0);
                      const stockLow  = currStock !== null && currStock <= 5;
                      const stockZero = currStock !== null && currStock <= 0;
                      const isArchived = ['Rejected','Issued','Closed'].includes(r.status);
                      return (
                      <tr key={r.id}
                        style={{ opacity: isArchived ? 0.55 : 1, fontStyle: isArchived ? 'italic' : 'normal', background: isArchived ? '#F8F6F3' : '#fff' }}
                        onMouseEnter={e => e.currentTarget.style.background='#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background=isArchived?'#F8F6F3':'#fff'}>
                        <Td>
                          <span style={{ fontFamily:'monospace', color:'#1B3B5F', fontWeight:700, cursor:'pointer', borderBottom:'1px dashed #1B3B5F' }} onClick={() => setCrDetail(r)}>
                            {r.requisitionId}
                          </span>
                        </Td>
                        <Td><strong>{r.itemName || r.item}</strong></Td>
                        <Td>{r.qty || r.quantity || '—'}</Td>
                        <Td>{currStock !== null ? <><strong style={{ color: stockZero ? '#a32d2d' : stockLow ? '#ba7517' : '#2C2C2C' }}>{currStock}</strong> <span style={{ fontSize:11, color:'#9B9B9B' }}>{r.unit || ''}</span></> : <span style={{ color:'#9B9B9B', fontSize:12 }}>—</span>}</Td>
                        <Td>{r.requestedBy}</Td>
                        <Td>{r.purpose}</Td>
                        <Td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : (r.date || '—')}</Td>
                        <Td><Badge label={r.status} type="req"/></Td>
                        <Td>
                          {r.status === 'Pending' && <>
                            <button style={{ ...Bs, padding:'6px 10px', fontSize:11, marginRight:5 }} disabled={saving} onClick={() => actReq('consumption',r.id,'Approved')}>Approve</button>
                            <button style={{ ...Bd, padding:'6px 10px', fontSize:11, marginRight:5 }} disabled={saving} onClick={() => handleRejectPR('consumption',r.id)}>Reject</button>
                          </>}
                          {r.status === 'Approved' && (
                            <button style={{ padding:'6px 10px', background:'#1B3B5F', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer' }} onClick={() => issueStockForCR(r)}>📤 Issue stock</button>
                          )}
                          {r.status === 'Rejected' && <span style={{ color:'#9B9B9B', fontSize:12, fontStyle:'italic' }}>Rejected</span>}
                          {r.status === 'Issued' && <span style={{ color:'#2D5F4F', fontSize:12, fontWeight:600 }}>✓ Issued</span>}
                        </Td>
                      </tr>
                      );
                    })}
                    {consumption.length === 0 && <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:'#9B9B9B' }}>No consumption requisitions found</td></tr>}
                  </tbody>
                </table>
              )}
              <Pagination page={crPage} total={consumption.length} perPage={invPerPage} onPage={setCrPage} onPerPage={setInvPerPage}/>
            </div>
          )}
          {tab === 'consumption' && (can('CREATE_CR')||can('APPROVE_CR')||(() => { try { return JSON.parse(localStorage.getItem('user')||'{}')?.role?.toUpperCase() === 'CANTEEN_INCHARGE'; } catch { return false; } })()) && (
            <p style={{ fontSize:12, color:'#9B9B9B', marginTop:12 }}>
              CRs can be raised even when stock is low or zero. Stock is issued only against an approved CR and cannot exceed current inventory at time of issue. Full consumption history is available in the <strong>Reports module</strong>.
            </p>
          )}

          {/* ══ STOCK ALERTS ══ */}
          {tab === 'alerts' && (
            <div style={{ background:'#fff', borderRadius:12, padding:32, boxShadow:'0 4px 20px rgba(0,0,0,0.07)' }}>
              <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#1B3B5F', marginBottom:24 }}>Stock Alerts</h2>
              {loading.items ? <Spinner/> : (() => {
                const alerts = inventory.filter(i => (i.currentStock ?? 0) <= (i.reorderLevel ?? 0));
                if (alerts.length === 0) return (
                  <div style={{ textAlign:'center', padding:60, color:'#9B9B9B' }}>
                    <div style={{ fontSize:48, marginBottom:16 }}>✅</div>All items are well stocked
                  </div>
                );
                return alerts.map(item => {
                  const st    = getStatus(item);
                  const stock = item.currentStock ?? 0;
                  return (
                    <div key={item.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderRadius:8, marginBottom:12, background:st.cls==='out'?'#FFF5F5':'#FFFBEB', border:`1px solid ${st.cls==='out'?'#FCA5A5':'#FCD34D'}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                        <span style={{ fontSize:22 }}>{st.cls==='out'?'🔴':'🟡'}</span>
                        <div>
                          <div style={{ fontWeight:700, fontSize:14, color:'#1B3B5F' }}>{item.name} <span style={{ fontFamily:'monospace', fontSize:12, color:'#9B9B9B' }}>({item.itemCode})</span></div>
                          <div style={{ fontSize:12, color:'#6B6B6B', marginTop:2 }}>Current: <strong>{stock} {item.unit}</strong> · Reorder Level: {item.reorderLevel} {item.unit}</div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                        <Badge label={st.label}/>
                        <button style={{ ...Bs, padding:'7px 14px', fontSize:11 }} onClick={() => { setSiModal(item); setSiF(emptySI); setTab('inventory'); }}>+ Add Stock</button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}

        </div>{/* /content */}
        <div style={{ background:'#333', color:'#fff', textAlign:'center', padding:14, fontSize:12 }}>
          2026 Copyright © Omkara Hostel. All Rights Reserved.
        </div>
      </main>


      {/* ══════ DIRECT STOCK IN MODAL ══════ */}
      {directModal && (
        <div style={OV}>
          <div style={{ ...MB, width:560 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Direct Stock In</h2>
                <p style={{ color:'#6B6B6B', fontSize:13, margin:'4px 0 0' }}>Stock received without a purchase requisition</p>
              </div>
              <button style={MX} onClick={() => setDirectModal(false)}>×</button>
            </div>
            <div style={MBd}>
              {/* Reason type */}
              <SecHead>Reason Type *</SecHead>
              <select style={{ ...IS, marginBottom:8 }} value={directReason} onChange={e => setDirectReason(e.target.value)}>
                <option value="">— Select reason —</option>
                <option value="emergency">Emergency purchase</option>
                <option value="direct">Routine direct buy</option>
                <option value="donation">Donation / complimentary</option>
                <option value="adjustment">Stock count adjustment</option>
              </select>
              {(directReason === 'emergency' || directReason === 'adjustment') && (
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'#fef6e4', border:'1px solid #f5d78e', borderRadius:20, fontSize:11, color:'#7a4f00', marginBottom:12 }}>
                  ⚡ Admin will be notified immediately
                </div>
              )}

              {/* Item details */}
              <SecHead>Item Details</SecHead>
              <div style={G2}>
                <div>
                  <label style={LS}>Item *</label>
                  <select style={IS} value={directF.item} onChange={e => {
                    const selected = inventory.find(i => i.itemCode === e.target.value);
                    setDirectF(f => ({ ...f, item:e.target.value, unit: selected?.unit || f.unit }));
                  }}>
                    <option value="">Select item</option>
                    {inventory.map(i => <option key={i.id} value={i.itemCode}>{i.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LS}>{directReason === 'adjustment' ? 'Adjustment Qty (+/−) *' : 'Quantity *'}</label>
                  <input style={IS} type="number" placeholder="0" value={directF.qty} onChange={e => setDirectF(f => ({ ...f, qty:e.target.value }))}/>
                </div>
                <div>
                  <label style={LS}>Unit</label>
                  <select style={IS} value={directF.unit} onChange={e => setDirectF(f => ({ ...f, unit:e.target.value }))}>
                    <option value="">Select unit</option>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                {directReason !== 'donation' && directReason !== 'adjustment' && (
                  <div>
                    <label style={LS}>Unit Price (₹)</label>
                    <input style={IS} type="number" placeholder="0.00" value={directF.unitPrice} onChange={e => setDirectF(f => ({ ...f, unitPrice:e.target.value }))}/>
                  </div>
                )}
              </div>

              {/* Receipt details */}
              <SecHead>Receipt Details</SecHead>
              <div style={G2}>
                <div>
                  <label style={LS}>{directReason === 'adjustment' ? 'Supporting Document' : directReason === 'donation' ? 'Receipt / Reference (optional)' : 'Invoice / Receipt No.'}</label>
                  <input style={IS} placeholder="INV or RCPT number" value={directF.invoice} onChange={e => setDirectF(f => ({ ...f, invoice:e.target.value }))}/>
                </div>
                <div>
                  <label style={LS}>Date Received *</label>
                  <input style={IS} type="date" value={directF.date} onChange={e => setDirectF(f => ({ ...f, date:e.target.value }))}/>
                </div>
                <div>
                  <label style={LS}>Received By *</label>
                  <input style={IS} placeholder="Name of person" value={directF.receivedBy} onChange={e => setDirectF(f => ({ ...f, receivedBy:e.target.value }))}/>
                </div>
                {directReason !== 'donation' && (
                  <div>
                    <label style={LS}>Supplier</label>
                    <select style={IS} value={directF.supplier} onChange={e => setDirectF(f => ({ ...f, supplier:e.target.value }))}>
                      <option value="">Select supplier</option>
                      {suppliers.map(s => <option key={s.supplierCode} value={s.supplierCode}>{s.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Mandatory note */}
              <SecHead>{directReason === 'donation' ? 'Source / Donor Details *' : directReason === 'adjustment' ? 'Reason for Adjustment *' : 'Reason / Remarks *'}</SecHead>
              <textarea style={{ ...IS, resize:'none', height:72 }} placeholder="Explain why stock is being added without a PR…" value={directF.note} onChange={e => setDirectF(f => ({ ...f, note:e.target.value }))}/>
            </div>
            <div style={MF}>
              <button style={{ ...Bg, flex:1 }} onClick={() => setDirectModal(false)}>Cancel</button>
              <button style={{ ...Bs, flex:2, background:'#D4AF37', color:'#1B3B5F', border:'none', opacity:saving?0.6:1 }} disabled={saving} onClick={async () => {
                if (!directReason) { alert('Please select a reason type.'); return; }
                if (!directF.item) { alert('Please select an item.'); return; }
                if (!directF.qty)  { alert('Quantity is required.'); return; }
                if (!directF.note.trim()) { alert('A mandatory note is required.'); return; }
                // Use existing stock-in service call
                const item = inventory.find(i => i.itemCode === directF.item);
                if (!item) return;
                setSaving(true);
                try {
                  await inventoryService.recordStockIn({
                    itemCode:      item.itemCode,
                    quantity:      +directF.qty,
                    unitPrice:     directF.unitPrice ? +directF.unitPrice : null,
                    supplierCode:  directF.supplier || null,
                    invoiceNumber: directF.invoice  || null,
                    receivedDate:  directF.date,
                    receivedBy:    directF.receivedBy || null,
                    type:          directReason.charAt(0).toUpperCase() + directReason.slice(1),
                    remarks:       `[${directReason.toUpperCase()}] ${directF.note}`,
                  });
                  await Promise.all([loadItems(), loadStockIn(), loadStats(), loadSuppliers()]);
                  setDirectModal(false);
                  setDirectF({ item:'', qty:'', unit:'', unitPrice:'', invoice:'', date:TODAY, receivedBy:'', supplier:'', note:'' });
                  setDirectReason('');
                  showToast(`✓ Stock added via Direct Stock In!`);
                } catch(e) {
                  showToast('Failed: ' + (e.response?.data?.message || e.message), 'error');
                } finally { setSaving(false); }
              }}>Add Stock</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ RECEIVE STOCK MODAL (against approved PR) ══════ */}

      {/* ══════ STOCK IN MODAL ══════ */}
      {siModal && (
        <div style={OV}>
          <div style={{ ...MB, width:640 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>{siModal._prId ? 'Receive Stock' : 'Add Stock (Stock In)'}</h2>
                <p style={{ color:'#6B6B6B', fontSize:13, margin:'4px 0 0' }}>
                  {siModal._prId
                    ? <>Recording receipt against <strong style={{ color:'#1B3B5F' }}>{siModal._prReqId}</strong></>
                    : <>Recording inventory for <strong>{siModal.name}</strong></>
                  }
                </p>
              </div>
              <button style={MX} onClick={() => setSiModal(null)}>×</button>
            </div>
            <div style={MBd}>
              {/* PR info strip — shown only when opened from Receive Stock */}
              {siModal._prId ? (
                <div style={{ background:'#F8F6F3', border:'1px solid #E8E4DF', borderRadius:6, padding:'12px 14px', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
                  {[['PR ID', siModal._prReqId], ['Item', siModal.name], ['Qty Requested', siModal._prQty ? `${siModal._prQty} ${siModal.unit}` : '—'], ['Unit', siModal.unit]].map(([l,v]) => (
                    <div key={l}>
                      <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.8px', color:'#9B9B9B', marginBottom:3 }}>{l}</div>
                      <div style={{ fontSize:13, fontWeight:500, color: l==='Qty Requested' ? '#ba7517' : '#1a2236' }}>{v}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background:'#F8F6F3', borderRadius:8, padding:18, marginBottom:4 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:14 }}>
                    <FieldRow label="Item Code"     value={siModal.itemCode}/>
                    <FieldRow label="Item Name"     value={siModal.name}/>
                    <FieldRow label="Current Stock" value={`${siModal.currentStock ?? 0} ${siModal.unit}`} color="#F59E0B"/>
                    <FieldRow label="Unit"          value={siModal.unit}/>
                  </div>
                </div>
              )}
              <SecHead>Receipt Details</SecHead>
              <div style={G2}>
                <div>
                  <label style={LS}>{siModal._prId ? 'Qty Actually Received *' : `Quantity Received (${siModal.unit}) *`}</label>
                  <input style={errBorder('qty')} type="number" min="0.01" step="0.01" placeholder="0" value={siF.qty} onChange={e => setSiF(p => ({ ...p, qty:e.target.value }))}/>
                  <ErrMsg name="qty"/>
                  {siModal._prId && siF.qty && siModal._prQty && +siF.qty < +siModal._prQty && (
                    <div style={{ background:'#fef6e4', border:'1px solid #f5d78e', borderRadius:6, padding:'8px 12px', marginTop:6, fontSize:12, color:'#7a4f00' }}>
                      ⚠️ Quantity received is less than requested. PR will be closed with a shortage note.
                    </div>
                  )}
                </div>
                <div>
                  <label style={LS}>Unit Price (₹)</label>
                  <input style={IS} type="number" min="0" step="0.01" placeholder="0.00" value={siF.unitPrice} onChange={e => setSiF(p => ({ ...p, unitPrice:e.target.value }))}/>
                </div>
              </div>
              {siF.qty && siF.unitPrice && +siF.qty > 0 && +siF.unitPrice > 0 && (
                <div style={{ background:'#F0FAF5', borderRadius:6, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#2D5F4F', fontWeight:600 }}>
                  Total Value: ₹{(+siF.qty * +siF.unitPrice).toLocaleString('en-IN', { maximumFractionDigits:2 })}
                </div>
              )}
              <div style={G2}>
                <div>
                  <label style={LS}>Invoice Number</label>
                  <input style={IS} placeholder="INV-2026-XXXX" value={siF.invoice} onChange={e => setSiF(p => ({ ...p, invoice:e.target.value }))}/>
                </div>
                <div>
                  <label style={LS}>Received Date *</label>
                  <input style={errBorder('date')} type="date" value={siF.date} onChange={e => setSiF(p => ({ ...p, date:e.target.value }))}/>
                  <ErrMsg name="date"/>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Received By *</label>
                <input style={errBorder('receivedBy')} placeholder="Name of person receiving" value={siF.receivedBy} onChange={e => setSiF(p => ({ ...p, receivedBy:cap(e.target.value) }))}/>
                <ErrMsg name="receivedBy"/>
              </div>
              <SecHead>Supplier Information</SecHead>
              <div style={{ marginBottom:12 }}>
                <label style={LS}>Select Supplier</label>
                <select style={IS} value={siF.supplierCode} onChange={e => setSiF(p => ({ ...p, supplierCode:e.target.value }))}>
                  <option value="">Select supplier (optional)...</option>
                  {suppliers.map(s => <option key={s.supplierCode} value={s.supplierCode}>{s.supplierCode} — {s.name}</option>)}
                  <option value="new">+ Add New Supplier</option>
                </select>
              </div>
              {siF.supplierCode === 'new' && (
                <div style={{ background:'#F8F6F3', padding:16, borderRadius:8, marginBottom:16 }}>
                  <div style={{ marginBottom:12 }}>
                    <label style={LS}>Supplier Name *</label>
                    <input style={errBorder('newSupName')} placeholder="Supplier name" value={siF.newSupName} onChange={e => setSiF(p => ({ ...p, newSupName:cap(e.target.value) }))}/>
                    <ErrMsg name="newSupName"/>
                  </div>
                  <div style={G2}>
                    <div>
                      <label style={LS}>Contact Person</label>
                      <input style={IS} placeholder="Contact name" value={siF.newSupContact} onChange={e => setSiF(p => ({ ...p, newSupContact:cap(e.target.value) }))}/>
                    </div>
                    <div>
                      <label style={LS}>Phone Number</label>
                      <input style={IS} type="tel" placeholder="Phone" value={siF.newSupPhone} onChange={e => setSiF(p => ({ ...p, newSupPhone:e.target.value }))}/>
                    </div>
                  </div>
                </div>
              )}
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Remarks (Optional)</label>
                <textarea style={{ ...IS, resize:'vertical' }} rows={3} placeholder="Quality check notes, batch number..." value={siF.remarks} onChange={e => setSiF(p => ({ ...p, remarks:e.target.value }))}/>
              </div>
              <div style={{ background:'#F8F6F3', borderRadius:8, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:14, color:'#6B6B6B' }}>New Stock Level:</span>
                <span style={{ fontSize:20, fontWeight:700, color:'#2D5F4F' }}>{siNew !== null ? `${siNew} ${siModal.unit}` : '—'}</span>
              </div>
            </div>
            <div style={MF}>
              <button style={{ ...Bg, flex:1 }} onClick={() => setSiModal(null)} disabled={saving}>Cancel</button>
              <button style={{ ...Bs, flex:2, opacity:saving?0.6:1 }} onClick={submitStockIn} disabled={saving}>{saving ? 'Saving…' : siModal._prId ? '📥 Confirm receipt & close PR' : 'Add Stock'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ STOCK OUT MODAL ══════ */}
      {soModal && (
        <div style={OV}>
          <div style={{ ...MB, width:600 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Issue Stock (Stock Out)</h2>
                <p style={{ color:'#6B6B6B', fontSize:13, margin:'4px 0 0' }}>Recording issue for <strong>{soModal.name}</strong></p>
              </div>
              <button style={MX} onClick={() => setSoModal(null)}>×</button>
            </div>
            <div style={MBd}>
              <div style={{ background:'#F8F6F3', borderRadius:8, padding:18, marginBottom:4 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:14 }}>
                  <FieldRow label="Item Code"       value={soModal.itemCode}/>
                  <FieldRow label="Item Name"       value={soModal.name}/>
                  <FieldRow label="Available Stock" value={`${soAvail} ${soModal.unit}`} color="#F59E0B"/>
                  <FieldRow label="Unit"            value={soModal.unit}/>
                </div>
              </div>
              <SecHead>Issue Details</SecHead>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Quantity to Issue ({soModal.unit}) *</label>
                <input style={errBorder('qty')} type="number" min="0.01" step="0.01" max={soAvail} placeholder="0" value={soF.qty} onChange={e => setSoF(p => ({ ...p, qty:e.target.value }))}/>
                <ErrMsg name="qty"/>
                {soRem !== null && soRem < 0 && <div style={{ color:'#DC3545', fontSize:12, marginTop:4 }}>⚠ Exceeds available stock!</div>}
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Issued To (Department / Person) *</label>
                <select style={errBorder('issuedTo')} value={soF.issuedTo} onChange={e => setSoF(p => ({ ...p, issuedTo:e.target.value }))}>
                  <option value="">Select department/person...</option>
                  {ISSUED_TO_OPTS.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Purpose *</label>
                <select style={errBorder('purpose')} value={soF.purpose} onChange={e => setSoF(p => ({ ...p, purpose:e.target.value }))}>
                  <option value="">Select purpose...</option>
                  {PURPOSE_OPTS.map(o => <option key={o}>{o}</option>)}
                </select>
                <ErrMsg name="purpose"/>
              </div>
              <div style={G2}>
                <div>
                  <label style={LS}>Issue Date *</label>
                  <input style={IS} type="date" value={soF.date} onChange={e => setSoF(p => ({ ...p, date:e.target.value }))}/>
                </div>
                <div>
                  <label style={LS}>Authorized By</label>
                  <input style={IS} placeholder="Manager / Warden name" value={soF.authorizedBy} onChange={e => setSoF(p => ({ ...p, authorizedBy:cap(e.target.value) }))}/>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Remarks (Optional)</label>
                <textarea style={{ ...IS, resize:'vertical' }} rows={3} placeholder="Additional notes..." value={soF.remarks} onChange={e => setSoF(p => ({ ...p, remarks:e.target.value }))}/>
              </div>
              <div style={{ background:'#F8F6F3', borderRadius:8, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:14, color:'#6B6B6B' }}>Remaining Stock:</span>
                <span style={{ fontSize:20, fontWeight:700, color:soRemC }}>{soRem !== null ? `${soRem} ${soModal.unit}` : '—'}</span>
              </div>
              {soRem !== null && soRem >= 0 && soRem <= (soModal.reorderLevel ?? 0) && (
                <div style={{ background:'#FFF3CD', borderLeft:'4px solid #F59E0B', padding:'12px 16px', borderRadius:6, marginTop:12, fontSize:13, color:'#856404' }}>
                  ⚠️ Stock will fall below reorder level. Consider creating a Purchase Requisition.
                </div>
              )}
            </div>
            <div style={MF}>
              <button style={{ ...Bg, flex:1 }} onClick={() => setSoModal(null)} disabled={saving}>Cancel</button>
              <button style={{ ...Bc, flex:1, opacity:saving?0.6:1 }} onClick={submitStockOut} disabled={saving}>{saving ? 'Saving…' : 'Issue Stock'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ STOCK IN DETAIL ══════ */}
      {siDetail && (
        <div style={OV}>
          <div style={{ ...MB, width:700 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Stock In Details</h2>
                <p style={{ color:'#6B6B6B', fontSize:13, margin:'4px 0 0' }}>Transaction ID: <strong style={{ color:'#1B3B5F' }}>{siDetail.transactionId}</strong></p>
              </div>
              <button style={MX} onClick={() => setSiDetail(null)}>×</button>
            </div>
            <div style={MBd}>
              <div style={{ background:'#F8F6F3', borderRadius:8, padding:16, marginBottom:4 }}>
                <div style={G2}>
                  <FieldRow label="Transaction Date" value={siDetail.date}/>
                  <div><span style={LS}>Payment Status</span><Badge label={siDetail.paymentStatus === 'Paid' ? 'Approved' : 'Pending'} type="req"/></div>
                </div>
              </div>
              <SecHead>Item Information</SecHead>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>
                <FieldRow label="Item Code"         value={siDetail.itemCode}/>
                <FieldRow label="Item Name"         value={siDetail.item}/>
                <FieldRow label="Quantity Received" value={`+${siDetail.qty} ${siDetail.unit}`} big color="#2D5F4F"/>
              </div>
              <SecHead>Financial Details</SecHead>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>
                <FieldRow label="Unit Price"     value={siDetail.unitPrice ? `₹${siDetail.unitPrice.toLocaleString()}` : '—'}/>
                <FieldRow label="Total Value"    value={siDetail.totalValue ? `₹${siDetail.totalValue.toLocaleString()}` : '—'} big color="#1B3B5F"/>
                <FieldRow label="Invoice Number" value={siDetail.invoice || '—'}/>
              </div>
              <SecHead>Supplier Information</SecHead>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>
                <FieldRow label="Supplier Name"   value={siDetail.supplier || '—'}/>
                <FieldRow label="Supplier Code"   value={siDetail.supplierCode || '—'}/>
                <FieldRow label="Contact Details" value={siDetail.supplierContact || '—'}/>
              </div>
              <SecHead>Receipt Information</SecHead>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>
                <FieldRow label="Received By"   value={siDetail.receivedBy || '—'}/>
                <FieldRow label="Quality Check" value={siDetail.qualityCheck || '—'} color="#2D5F4F"/>
              </div>
              <SecHead>Additional Notes</SecHead>
              <div style={{ background:'#F8F6F3', borderRadius:6, padding:'12px 16px', fontSize:14, color:'#2C2C2C', lineHeight:1.6, marginBottom:16 }}>
                {siDetail.remarks || 'No remarks'}
              </div>
              <div style={{ background:'linear-gradient(135deg,#2D5F4F,#25503F)', padding:20, borderRadius:8 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:20, alignItems:'center', color:'#fff', textAlign:'center' }}>
                  <div><div style={{ fontSize:11, opacity:.8, marginBottom:4 }}>Previous Stock</div><div style={{ fontSize:24, fontWeight:700 }}>{siDetail.prevStock !== undefined ? `${siDetail.prevStock} ${siDetail.unit}` : '—'}</div></div>
                  <div style={{ fontSize:28, opacity:.5 }}>→</div>
                  <div><div style={{ fontSize:11, opacity:.8, marginBottom:4 }}>New Stock</div><div style={{ fontSize:24, fontWeight:700 }}>{siDetail.newStock !== undefined ? `${siDetail.newStock} ${siDetail.unit}` : '—'}</div></div>
                </div>
              </div>
            </div>
            <div style={{ ...MF, justifyContent:'space-between' }}>
              <div style={{ display:'flex', gap:10 }}>
                <button style={Bp} onClick={() => window.print()}>🖨️ Print</button>
                <button style={Bc} onClick={() => alert('PDF export would be generated.')}>📄 Export PDF</button>
              </div>
              <button style={Bg} onClick={() => setSiDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ STOCK OUT DETAIL ══════ */}
      {soDetail && (
        <div style={OV}>
          <div style={{ ...MB, width:700 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Stock Out Details</h2>
                <p style={{ color:'#6B6B6B', fontSize:13, margin:'4px 0 0' }}>Transaction ID: <strong style={{ color:'#1B3B5F' }}>{soDetail.transactionId}</strong></p>
              </div>
              <button style={MX} onClick={() => setSoDetail(null)}>×</button>
            </div>
            <div style={MBd}>
              <div style={{ background:'#F8F6F3', borderRadius:8, padding:16, marginBottom:4 }}>
                <div style={G2}>
                  <FieldRow label="Issue Date" value={soDetail.date}/>
                  <div><span style={LS}>Status</span><Badge label="Approved" type="req"/></div>
                </div>
              </div>
              <SecHead>Item Information</SecHead>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>
                <FieldRow label="Item Code"       value={soDetail.itemCode}/>
                <FieldRow label="Item Name"       value={soDetail.item}/>
                <FieldRow label="Quantity Issued" value={`−${soDetail.qty} ${soDetail.unit}`} big color="#E85D4A"/>
              </div>
              <SecHead>Issue Information</SecHead>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>
                <FieldRow label="Issued To"      value={soDetail.issuedTo}/>
                <FieldRow label="Purpose"        value={soDetail.purpose}/>
                <FieldRow label="Authorized By"  value={soDetail.authorizedBy || '—'}/>
                <FieldRow label="Issued By"      value={soDetail.issuedByPerson || '—'}/>
                <FieldRow label="Requisition ID" value={soDetail.requisitionId || '—'}/>
              </div>
              <SecHead>Additional Notes</SecHead>
              <div style={{ background:'#F8F6F3', borderRadius:6, padding:'12px 16px', fontSize:14, color:'#2C2C2C', lineHeight:1.6, marginBottom:16 }}>
                {soDetail.remarks || 'No remarks'}
              </div>
              <div style={{ background:'linear-gradient(135deg,#E85D4A,#C74A39)', padding:20, borderRadius:8 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:20, alignItems:'center', color:'#fff', textAlign:'center' }}>
                  <div><div style={{ fontSize:11, opacity:.8, marginBottom:4 }}>Previous Stock</div><div style={{ fontSize:24, fontWeight:700 }}>{soDetail.prevStock !== undefined ? `${soDetail.prevStock} ${soDetail.unit}` : '—'}</div></div>
                  <div style={{ fontSize:28, opacity:.5 }}>→</div>
                  <div><div style={{ fontSize:11, opacity:.8, marginBottom:4 }}>Remaining Stock</div><div style={{ fontSize:24, fontWeight:700 }}>{soDetail.remStock !== undefined ? `${soDetail.remStock} ${soDetail.unit}` : '—'}</div></div>
                </div>
              </div>
              {soDetail.lowStock && (
                <div style={{ background:'#FFF3CD', borderLeft:'4px solid #F59E0B', padding:'14px 18px', borderRadius:6, marginTop:14, display:'flex', gap:12, alignItems:'flex-start' }}>
                  <span style={{ fontSize:22 }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight:700, color:'#856404', marginBottom:3 }}>Low Stock Warning</div>
                    <div style={{ fontSize:13, color:'#6B6B6B' }}>Stock is below reorder level. Consider initiating a purchase requisition.</div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ ...MF, justifyContent:'space-between' }}>
              <div style={{ display:'flex', gap:10 }}>
                <button style={Bp} onClick={() => window.print()}>🖨️ Print</button>
                <button style={Bc} onClick={() => alert('PDF export would be generated.')}>📄 Export PDF</button>
              </div>
              <button style={Bg} onClick={() => setSoDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ PURCHASE REQUISITION DETAIL ══════ */}
      {prDetail && (
        <div style={OV}>
          <div style={{ ...MB, width:800 }}>
            <div style={{ padding:'24px 30px', borderBottom:'1px solid #E8E4DF', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <h2 style={MT}>Purchase Requisition Details</h2>
              <button style={MX} onClick={() => setPrDetail(null)}>×</button>
            </div>
            <div style={{ overflowY:'auto', padding:'32px 40px', flex:1 }}>
              <div style={{ textAlign:'center', marginBottom:28, paddingBottom:20, borderBottom:'3px solid #1B3B5F' }}>
                <h1 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:34, color:'#1B3B5F', margin:'0 0 4px', letterSpacing:3 }}>OMKARA HOSTEL</h1>
                <p style={{ fontSize:11, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:2, margin:0 }}>Purchase Requisition Form</p>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:28 }}>
                <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 5px', letterSpacing:1, fontWeight:700 }}>Requisition ID</p><p style={{ fontSize:18, fontWeight:600, color:'#1B3B5F', margin:0 }}>{prDetail.requisitionId}</p></div>
                <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 5px', letterSpacing:1, fontWeight:700 }}>Date Requested</p><p style={{ fontSize:16, color:'#2C2C2C', margin:0 }}>{prDetail.date}</p></div>
                <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 5px', letterSpacing:1, fontWeight:700 }}>Requested By</p><p style={{ fontSize:16, color:'#2C2C2C', margin:0 }}>{prDetail.requestedBy}</p></div>
                <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 5px', letterSpacing:1, fontWeight:700 }}>Status</p><Badge label={prDetail.status} type="req"/></div>
              </div>
              <div style={{ background:'#F8F6F3', padding:24, borderRadius:8, marginBottom:24 }}>
                <h3 style={{ fontSize:13, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', margin:'0 0 18px', fontWeight:700 }}>Item Information</h3>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:18, marginBottom:14 }}>
                  <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 4px' }}>Item Name</p><p style={{ fontSize:16, fontWeight:600, color:'#2C2C2C', margin:0 }}>{prDetail.item}</p></div>
                  <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 4px' }}>Quantity</p><p style={{ fontSize:16, fontWeight:600, color:'#2C2C2C', margin:0 }}>{prDetail.qty}</p></div>
                  <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 4px' }}>Unit Price</p><p style={{ fontSize:16, fontWeight:600, color:'#2C2C2C', margin:0 }}>{prDetail.unitPrice}</p></div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>
                  <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 4px' }}>Preferred Supplier</p><p style={{ fontSize:14, color:'#2C2C2C', margin:0 }}>{prDetail.supplier}</p></div>
                  <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 4px' }}>Total Estimated Cost</p><p style={{ fontSize:20, fontWeight:700, color:'#1B3B5F', margin:0 }}>{prDetail.totalCost}</p></div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
                <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 5px', letterSpacing:1, fontWeight:700 }}>Reason</p><p style={{ fontSize:14, color:'#2C2C2C', margin:0 }}>{prDetail.reason}</p></div>
                <div><p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 5px', letterSpacing:1, fontWeight:700 }}>Date Required</p><p style={{ fontSize:14, color:'#2C2C2C', margin:0 }}>{prDetail.dateRequired}</p></div>
              </div>
              <div style={{ marginBottom:24 }}>
                <p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 8px', letterSpacing:1, fontWeight:700 }}>Remarks</p>
                <div style={{ background:'#fff', padding:14, borderRadius:6, border:'2px solid #E8E4DF', fontSize:14, color:'#2C2C2C', minHeight:60 }}>{prDetail.remarks || '—'}</div>
              </div>
              {prDetail.approvedBy && (
                <div style={{ background:'#F0F9FF', padding:18, borderRadius:8, borderLeft:'4px solid #1B3B5F', marginBottom:24 }}>
                  <p style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B', margin:'0 0 8px', letterSpacing:1, fontWeight:700 }}>{prDetail.status === 'Rejected' ? 'Rejection Details' : 'Approval Details'}</p>
                  <p style={{ fontSize:14, color:'#2C2C2C', margin:0, lineHeight:1.8 }}>
                    <strong>{prDetail.status === 'Rejected' ? 'Rejected by' : 'Approved by'}:</strong> {prDetail.approvedBy}<br/>
                    <strong>Date:</strong> {prDetail.approvalDate}<br/>
                    <strong>Comments:</strong> {prDetail.approvalComments}
                  </p>
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:36, marginTop:48 }}>
                {['Requested By','Verified By','Approved By'].map(s => (
                  <div key={s} style={{ borderTop:'2px solid #2C2C2C', paddingTop:10 }}>
                    <span style={{ fontSize:11, textTransform:'uppercase', color:'#6B6B6B' }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding:'18px 30px', borderTop:'2px solid #E8E4DF', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div style={{ display:'flex', gap:10 }}>
                <button style={Bp} onClick={() => window.print()}>🖨️ Print</button>
                <button style={Bg} onClick={() => alert('PDF export here.')}>📄 Export PDF</button>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                {prDetail.status === 'Pending' && <>
                  {can('APPROVE_PR') && (
                    <button style={{ ...Bs, padding:'12px 24px', opacity:saving?0.6:1 }} disabled={saving} onClick={() => { if (window.confirm(`Approve ${prDetail.requisitionId}?`)) actReq('purchase', prDetail.id, 'Approved'); }}>✓ Approve</button>
                  )}
                  <button style={{ ...Bd, padding:'12px 24px', opacity:saving?0.6:1 }} disabled={saving} onClick={() => handleRejectPR('purchase', prDetail.id)}>✗ Reject</button>
                </>}
                <button style={Bg} onClick={() => setPrDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ CREATE PURCHASE REQUISITION ══════ */}
      {prCreate && (
        <div style={OV}>
          <div style={{ ...MB, width:600 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Create Purchase Requisition</h2>
                <p style={{ color:'#6B6B6B', fontSize:13, margin:'4px 0 0' }}>Request approval to purchase new stock</p>
              </div>
              <button style={MX} onClick={() => setPrCreate(false)}>×</button>
            </div>
            <div style={MBd}>
              <div style={{ marginBottom:18 }}>
                <label style={LS}>Item *</label>
                <select style={errBorder('item')} value={prF.item} onChange={e => setPrF(p => ({ ...p, item:e.target.value }))}>
                  <option value="">Select item...</option>
                  {inventory.map(i => <option key={i.id} value={`${i.itemCode} — ${i.name}`}>{i.itemCode} — {i.name}</option>)}
                  <option value="new">+ Add New Item</option>
                </select>
                <ErrMsg name="item"/>
              </div>
              {prF.item === 'new' && (
                <div style={{ background:'#F8F6F3', padding:18, borderRadius:8, marginBottom:18 }}>
                  <div style={{ marginBottom:14 }}>
                    <label style={LS}>Item Name *</label>
                    <input style={errBorder('newItemName')} placeholder="Enter item name" value={prF.newItemName} onChange={e => setPrF(p => ({ ...p, newItemName:cap(e.target.value) }))}/>
                    <ErrMsg name="newItemName"/>
                  </div>
                  <div style={G2}>
                    <div><label style={LS}>Category</label><select style={IS} value={prF.newItemCat} onChange={e => setPrF(p => ({ ...p, newItemCat:e.target.value }))}>{CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}</select></div>
                    <div><label style={LS}>Unit</label><select style={IS} value={prF.newItemUnit} onChange={e => setPrF(p => ({ ...p, newItemUnit:e.target.value }))}>{UNITS.map(u => <option key={u}>{u}</option>)}</select></div>
                  </div>
                </div>
              )}
              <div style={G2}>
                <div>
                  <label style={LS}>Quantity Required *</label>
                  <input style={errBorder('qty')} type="number" min="1" placeholder="0" value={prF.qty} onChange={e => setPrF(p => ({ ...p, qty:e.target.value }))}/>
                  <ErrMsg name="qty"/>
                </div>
                <div>
                  <label style={LS}>Est. Unit Price (₹)</label>
                  <input style={IS} type="number" min="0" step="0.01" placeholder="0.00" value={prF.unitPrice} onChange={e => setPrF(p => ({ ...p, unitPrice:e.target.value }))}/>
                </div>
              </div>
              {prF.qty && prF.unitPrice && +prF.qty > 0 && +prF.unitPrice > 0 && (
                <div style={{ background:'#F0FAF5', borderRadius:6, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#2D5F4F', fontWeight:600 }}>
                  Estimated Total: ₹{(+prF.qty * +prF.unitPrice).toLocaleString('en-IN', { maximumFractionDigits:2 })}
                </div>
              )}
              <div style={{ marginBottom:18 }}>
                <label style={LS}>Preferred Supplier</label>
                <select style={IS} value={prF.supplierCode} onChange={e => setPrF(p => ({ ...p, supplierCode:e.target.value, showAddSupplier: e.target.value === 'new' ? true : p.showAddSupplier }))}>
                  <option value="">Select supplier (optional)...</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.supplierCode}>{s.supplierCode} — {s.name}</option>
                  ))}
                </select>
                {/* Add New Supplier toggle */}
                {!prF.showAddSupplier && (
                  <button type="button" onClick={() => setPrF(p => ({ ...p, showAddSupplier:true, supplierCode:'new' }))}
                    style={{ marginTop:10, background:'none', border:'2px dashed #D4AF37', borderRadius:6, padding:'8px 14px', fontSize:12, fontWeight:700, color:'#1B3B5F', cursor:'pointer', width:'100%', textAlign:'left', letterSpacing:.5 }}>
                    + Add New Supplier
                  </button>
                )}
                {prF.showAddSupplier && (
                  <div style={{ marginTop:12, background:'#F8F6F3', border:'1px solid #E8E4DF', borderRadius:8, padding:'16px 18px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                      <span style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#1B3B5F' }}>New Supplier Details</span>
                      <button type="button" onClick={() => setPrF(p => ({ ...p, showAddSupplier:false, supplierCode:'', newSupplierName:'', newSupplierContact:'', newSupplierPhone:'' }))}
                        style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#9B9B9B', lineHeight:1 }}>×</button>
                    </div>
                    <div style={{ marginBottom:12 }}>
                      <label style={LS}>Supplier Name *</label>
                      <input style={IS} placeholder="e.g. Metro Wholesale" value={prF.newSupplierName} onChange={e => setPrF(p => ({ ...p, newSupplierName:cap(e.target.value) }))}/>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                      <div>
                        <label style={LS}>Contact Person</label>
                        <input style={IS} placeholder="e.g. Mr. Ramesh" value={prF.newSupplierContact} onChange={e => setPrF(p => ({ ...p, newSupplierContact:cap(e.target.value) }))}/>
                      </div>
                      <div>
                        <label style={LS}>Phone Number</label>
                        <input style={IS} type="tel" placeholder="e.g. 9876543210" value={prF.newSupplierPhone} onChange={e => setPrF(p => ({ ...p, newSupplierPhone:e.target.value }))}/>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginBottom:18 }}>
                <label style={LS}>Reason *</label>
                <select style={errBorder('reason')} value={prF.reason} onChange={e => setPrF(p => ({ ...p, reason:e.target.value }))}>
                  <option value="">Select reason...</option>
                  {['Reorder (Low Stock)','New Item','Bulk Purchase','Urgent Requirement'].map(r => <option key={r}>{r}</option>)}
                </select>
                <ErrMsg name="reason"/>
              </div>
              <div style={{ marginBottom:18 }}>
                <label style={LS}>Date Required</label>
                <input style={IS} type="date" value={prF.dateRequired} onChange={e => setPrF(p => ({ ...p, dateRequired:e.target.value }))}/>
              </div>
              <div>
                <label style={LS}>Remarks (Optional)</label>
                <textarea style={{ ...IS, resize:'vertical' }} rows={3} placeholder="Any additional notes..." value={prF.remarks} onChange={e => setPrF(p => ({ ...p, remarks:e.target.value }))}/>
              </div>
            </div>
            <div style={MF}>
              <button style={{ ...Bg, flex:1 }} onClick={() => setPrCreate(false)} disabled={saving}>Cancel</button>
              <button style={{ ...Bs, flex:1, opacity:saving?0.6:1 }} onClick={submitPR} disabled={saving}>{saving ? 'Submitting…' : 'Submit Requisition'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ CREATE CONSUMPTION REQUISITION ══════ */}
      {crCreate && (
        <div style={OV}>
          <div style={{ ...MB, width:580 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Create Consumption Requisition</h2>
                <p style={{ margin:'4px 0 0', color:'#6B6B6B', fontSize:13 }}>Request items for department use</p>
              </div>
              <button style={MX} onClick={() => { setCrCreate(false); setCrF(emptyCR); }}>×</button>
            </div>
            <div style={MBd}>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Item *</label>
                <select style={errBorder('item')} value={crF.item} onChange={e => setCrF(p => ({ ...p, item:e.target.value }))}>
                  <option value="">Select item...</option>
                  {inventory.filter(i => i.isActive !== false).map(i => (
                    <option key={i.id} value={`${i.itemCode} — ${i.name}`}>{i.itemCode} — {i.name} ({i.currentStock} {i.unit} available)</option>
                  ))}
                </select>
                <ErrMsg name="item"/>
              </div>
              <div style={G2}>
                <div>
                  <label style={LS}>Quantity Required *</label>
                  <input style={errBorder('qty')} type="number" min="0" step="0.01" placeholder="0" value={crF.qty} onChange={e => setCrF(p => ({ ...p, qty:e.target.value }))}/>
                  <ErrMsg name="qty"/>
                </div>
                <div>
                  <label style={LS}>Department *</label>
                  <select style={errBorder('department')} value={crF.department} onChange={e => setCrF(p => ({ ...p, department:e.target.value }))}>
                    <option value="">Select department...</option>
                    {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                  <ErrMsg name="department"/>
                </div>
              </div>
              <div style={G2}>
                <div>
                  <label style={LS}>Purpose *</label>
                  <select style={errBorder('purpose')} value={crF.purpose} onChange={e => setCrF(p => ({ ...p, purpose:e.target.value }))}>
                    <option value="">Select purpose...</option>
                    {PURPOSE_OPTS.map(p => <option key={p}>{p}</option>)}
                  </select>
                  <ErrMsg name="purpose"/>
                </div>
                <div>
                  <label style={LS}>Date Required *</label>
                  <input style={IS} type="date" value={crF.dateRequired} onChange={e => setCrF(p => ({ ...p, dateRequired:e.target.value }))}/>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Requested By *</label>
                <input style={errBorder('requestedBy')} placeholder="Your name / designation" value={crF.requestedBy} onChange={e => setCrF(p => ({ ...p, requestedBy:cap(e.target.value) }))}/>
                <ErrMsg name="requestedBy"/>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Remarks (Optional)</label>
                <textarea style={{ ...IS, resize:'vertical' }} rows={3} placeholder="Additional notes..." value={crF.remarks} onChange={e => setCrF(p => ({ ...p, remarks:e.target.value }))}/>
              </div>
            </div>
            <div style={MF}>
              <button style={{ ...Bg, flex:1 }} onClick={() => { setCrCreate(false); setCrF(emptyCR); }}>Cancel</button>
              <button style={{ ...Bp, flex:1, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={submitCR}>
                {saving ? 'Submitting...' : '📋 Submit Requisition'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ CR DETAILS MODAL ══════ */}
      {crDetail && (
        <div style={OV}>
          <div style={{ ...MB, width:660 }}>
            <div style={MH}>
              <div>
                <h2 style={MT}>Consumption Requisition Details</h2>
                <p style={{ margin:'4px 0 0', color:'#6B6B6B', fontSize:13 }}>Req ID: <strong>{crDetail.requisitionId}</strong></p>
              </div>
              <button style={MX} onClick={() => setCrDetail(null)}>×</button>
            </div>
            <div style={MBd}>
              {/* Summary bar */}
              <div style={{ background:'#F8F6F3', padding:'16px 20px', borderRadius:8, marginBottom:20, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div>
                  <span style={{ color:'#6B6B6B', fontSize:11, textTransform:'uppercase', letterSpacing:1 }}>Request Date</span>
                  <div style={{ fontWeight:600, marginTop:4 }}>
                    {crDetail.createdAt ? new Date(crDetail.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : (crDetail.date || '—')}
                  </div>
                </div>
                <div>
                  <span style={{ color:'#6B6B6B', fontSize:11, textTransform:'uppercase', letterSpacing:1 }}>Status</span>
                  <div style={{ marginTop:4 }}><Badge label={crDetail.status} type="req"/></div>
                </div>
              </div>

              {/* Item Info */}
              <h3 style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', borderBottom:'2px solid #E8E4DF', paddingBottom:8, marginBottom:14 }}>Item Information</h3>
              <div style={{ ...G2, marginBottom:20 }}>
                <div><span style={{ color:'#6B6B6B', fontSize:12 }}>Item Name</span><div style={{ fontWeight:600, marginTop:3 }}>{crDetail.itemName || crDetail.item || '—'}</div></div>
                <div><span style={{ color:'#6B6B6B', fontSize:12 }}>Quantity Requested</span><div style={{ fontWeight:700, fontSize:18, color:'#1B3B5F', marginTop:3 }}>{crDetail.quantity || crDetail.qty || '—'}</div></div>
              </div>

              {/* Requester Info */}
              <h3 style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', borderBottom:'2px solid #E8E4DF', paddingBottom:8, marginBottom:14 }}>Requester Information</h3>
              <div style={{ ...G2, marginBottom:20 }}>
                <div><span style={{ color:'#6B6B6B', fontSize:12 }}>Requested By</span><div style={{ fontWeight:600, marginTop:3 }}>{crDetail.requestedBy || '—'}</div></div>
                <div><span style={{ color:'#6B6B6B', fontSize:12 }}>Department</span><div style={{ fontWeight:600, marginTop:3 }}>{crDetail.department || '—'}</div></div>
                <div><span style={{ color:'#6B6B6B', fontSize:12 }}>Purpose</span><div style={{ fontWeight:600, marginTop:3 }}>{crDetail.purpose || '—'}</div></div>
                <div><span style={{ color:'#6B6B6B', fontSize:12 }}>Date Required</span><div style={{ fontWeight:600, marginTop:3 }}>{crDetail.dateRequired || '—'}</div></div>
              </div>

              {/* Remarks */}
              <h3 style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', borderBottom:'2px solid #E8E4DF', paddingBottom:8, marginBottom:14 }}>Remarks</h3>
              <div style={{ background:'#F8F6F3', padding:'12px 16px', borderRadius:6, marginBottom:20, fontSize:14, color:'#2C2C2C', lineHeight:1.6 }}>
                {crDetail.remarks || crDetail.approvalComments || 'No remarks'}
              </div>

              {/* Approval details if actioned */}
              {crDetail.approvedBy && (
                <>
                  <h3 style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', borderBottom:'2px solid #E8E4DF', paddingBottom:8, marginBottom:14 }}>Approval Details</h3>
                  <div style={{ background: crDetail.status === 'Approved' ? '#F0FDF4' : '#FFF5F5', padding:'14px 18px', borderRadius:8, borderLeft:`4px solid ${crDetail.status === 'Approved' ? '#2D5F4F' : '#DC3545'}`, marginBottom:20, fontSize:14 }}>
                    <div><strong>Actioned by:</strong> {crDetail.approvedBy}</div>
                    {crDetail.approvalDate && <div style={{ marginTop:4 }}><strong>Date:</strong> {crDetail.approvalDate}</div>}
                    {crDetail.approvalComments && <div style={{ marginTop:4 }}><strong>Comments:</strong> {crDetail.approvalComments}</div>}
                  </div>
                </>
              )}
            </div>
            <div style={MF}>
              <button style={{ ...Bp, flex:1 }} onClick={() => window.print()}>🖨️ Print</button>
              {crDetail.status === 'Pending' && <>
                <button style={{ ...Bs, flex:1, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={() => { actReq('consumption', crDetail.id, 'Approved'); setCrDetail(null); }}>✓ Approve</button>
                <button style={{ ...Bd, flex:1, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={() => { handleRejectPR('consumption', crDetail.id); setCrDetail(null); }}>✗ Reject</button>
              </>}
              {crDetail.status === 'Approved' && (
                <button style={{ ...Bw, flex:1 }} onClick={() => { issueStockForCR(crDetail); setCrDetail(null); }}>📦 Issue Stock</button>
              )}
              <button style={{ ...Bg, flex:1 }} onClick={() => setCrDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ ADD NEW ITEM ══════ */}
      {addItem && (
        <div style={OV}>
          <div style={{ ...MB, width:520 }}>
            <div style={MH}>
              <h2 style={MT}>Add New Item</h2>
              <button style={MX} onClick={() => setAddItem(false)}>×</button>
            </div>
            <div style={MBd}>
              <div style={G2}>
                <div>
                  <label style={LS}>Item Code *</label>
                  <input style={errBorder('code')} placeholder="e.g. SALT-011" value={aiF.code} onChange={e => setAiF(p => ({ ...p, code:e.target.value.toUpperCase() }))}/>
                  <ErrMsg name="code"/>
                </div>
                <div>
                  <label style={LS}>Item Name *</label>
                  <input style={errBorder('name')} placeholder="e.g. Salt" value={aiF.name} onChange={e => setAiF(p => ({ ...p, name:cap(e.target.value) }))}/>
                  <ErrMsg name="name"/>
                </div>
              </div>
              <div style={G2}>
                <div><label style={LS}>Category</label><select style={IS} value={aiF.category} onChange={e => setAiF(p => ({ ...p, category:e.target.value }))}>{CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}</select></div>
                <div><label style={LS}>Unit</label><select style={IS} value={aiF.unit} onChange={e => setAiF(p => ({ ...p, unit:e.target.value }))}>{UNITS.map(u => <option key={u}>{u}</option>)}</select></div>
              </div>
              <div style={G2}>
                <div>
                  <label style={LS}>Opening Stock (leave 0 if starting fresh)</label>
                  <input style={errBorder('stock')} type="number" min="0" placeholder="0" value={aiF.stock} onChange={e => setAiF(p => ({ ...p, stock:e.target.value }))}/>
                  <div style={{ fontSize:11, color:'#9B9B9B', marginTop:3 }}>Set to 0 — add stock via Purchase Requisition or Direct Stock In</div>
                  <ErrMsg name="stock"/>
                </div>
                <div>
                  <label style={LS}>Reorder Level</label>
                  <input style={errBorder('reorder')} type="number" min="0" placeholder="10" value={aiF.reorder} onChange={e => setAiF(p => ({ ...p, reorder:e.target.value }))}/>
                  <ErrMsg name="reorder"/>
                </div>
              </div>
            </div>
            <div style={MF}>
              <button style={{ ...Bg, flex:1 }} onClick={() => setAddItem(false)} disabled={saving}>Cancel</button>
              <button style={{ ...Bp, flex:1, opacity:saving?0.6:1 }} onClick={submitAddItem} disabled={saving}>{saving ? 'Adding…' : 'Add Item'}</button>
            </div>
          </div>
        </div>
      )}

    </div>

      {/* ── Toast Notification ── */}
      <Toast toast={toast}/>
      <style>{`
        @keyframes slideInToast {
          from { opacity:0; transform:translateY(-20px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </>
  );
};

export default InventoryScreen;